'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useHandleLoginSuccessAction, useLogStoreState } from '../../stores/userStore';

// ============================================================================
// GLOBAL DATA - COUNTRIES AND STATES (COMPREHENSIVE LIST)
// ============================================================================

const countryCodes = [
  // North America
  { code: '+1', country: 'United States', flag: '🇺🇸' },
  { code: '+1', country: 'Canada', flag: '🇨🇦' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽' },
  { code: '+502', country: 'Guatemala', flag: '🇬🇹' },
  { code: '+503', country: 'El Salvador', flag: '🇸🇻' },
  { code: '+504', country: 'Honduras', flag: '🇭🇳' },
  { code: '+505', country: 'Nicaragua', flag: '🇳🇮' },
  { code: '+506', country: 'Costa Rica', flag: '🇨🇷' },
  { code: '+507', country: 'Panama', flag: '🇵🇦' },
  { code: '+508', country: 'Saint Pierre and Miquelon', flag: '🇵🇲' },
  { code: '+509', country: 'Haiti', flag: '🇭🇹' },
  { code: '+590', country: 'Guadeloupe', flag: '🇬🇵' },
  { code: '+591', country: 'Bolivia', flag: '🇧🇴' },
  { code: '+592', country: 'Guyana', flag: '🇬🇾' },
  { code: '+593', country: 'Ecuador', flag: '🇪🇨' },
  { code: '+594', country: 'French Guiana', flag: '🇬🇫' },
  { code: '+595', country: 'Paraguay', flag: '🇵🇾' },
  { code: '+596', country: 'Martinique', flag: '🇲🇶' },
  { code: '+597', country: 'Suriname', flag: '🇸🇷' },
  { code: '+598', country: 'Uruguay', flag: '🇺🇾' },
  
  // South America
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷' },
  { code: '+56', country: 'Chile', flag: '🇨🇱' },
  { code: '+57', country: 'Colombia', flag: '🇨🇴' },
  { code: '+51', country: 'Peru', flag: '🇵🇪' },
  { code: '+58', country: 'Venezuela', flag: '🇻🇪' },
  
  // Europe
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
  { code: '+32', country: 'Belgium', flag: '🇧🇪' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
  { code: '+43', country: 'Austria', flag: '🇦🇹' },
  { code: '+45', country: 'Denmark', flag: '🇩🇰' },
  { code: '+46', country: 'Sweden', flag: '🇸🇪' },
  { code: '+47', country: 'Norway', flag: '🇳🇴' },
  { code: '+358', country: 'Finland', flag: '🇫🇮' },
  { code: '+48', country: 'Poland', flag: '🇵🇱' },
  { code: '+420', country: 'Czech Republic', flag: '🇨🇿' },
  { code: '+421', country: 'Slovakia', flag: '🇸🇰' },
  { code: '+36', country: 'Hungary', flag: '🇭🇺' },
  { code: '+40', country: 'Romania', flag: '🇷🇴' },
  { code: '+359', country: 'Bulgaria', flag: '🇧🇬' },
  { code: '+30', country: 'Greece', flag: '🇬🇷' },
  { code: '+351', country: 'Portugal', flag: '🇵🇹' },
  { code: '+353', country: 'Ireland', flag: '🇮🇪' },
  { code: '+354', country: 'Iceland', flag: '🇮🇸' },
  { code: '+355', country: 'Albania', flag: '🇦🇱' },
  { code: '+356', country: 'Malta', flag: '🇲🇹' },
  { code: '+357', country: 'Cyprus', flag: '🇨🇾' },
  { code: '+370', country: 'Lithuania', flag: '🇱🇹' },
  { code: '+371', country: 'Latvia', flag: '🇱🇻' },
  { code: '+372', country: 'Estonia', flag: '🇪🇪' },
  { code: '+373', country: 'Moldova', flag: '🇲🇩' },
  { code: '+374', country: 'Armenia', flag: '🇦🇲' },
  { code: '+375', country: 'Belarus', flag: '🇧🇾' },
  { code: '+376', country: 'Andorra', flag: '🇦🇩' },
  { code: '+377', country: 'Monaco', flag: '🇲🇨' },
  { code: '+378', country: 'San Marino', flag: '🇸🇲' },
  { code: '+380', country: 'Ukraine', flag: '🇺🇦' },
  { code: '+381', country: 'Serbia', flag: '🇷🇸' },
  { code: '+382', country: 'Montenegro', flag: '🇲🇪' },
  { code: '+383', country: 'Kosovo', flag: '🇽🇰' },
  { code: '+385', country: 'Croatia', flag: '🇭🇷' },
  { code: '+386', country: 'Slovenia', flag: '🇸🇮' },
  { code: '+387', country: 'Bosnia and Herzegovina', flag: '🇧🇦' },
  { code: '+389', country: 'North Macedonia', flag: '🇲🇰' },
  
  // Asia
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
  { code: '+66', country: 'Thailand', flag: '🇹🇭' },
  { code: '+84', country: 'Vietnam', flag: '🇻🇳' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭' },
  { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
  { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
  { code: '+880', country: 'Bangladesh', flag: '🇧🇩' },
  { code: '+94', country: 'Sri Lanka', flag: '🇱🇰' },
  { code: '+95', country: 'Myanmar', flag: '🇲🇲' },
  { code: '+855', country: 'Cambodia', flag: '🇰🇭' },
  { code: '+856', country: 'Laos', flag: '🇱🇦' },
  { code: '+852', country: 'Hong Kong', flag: '🇭🇰' },
  { code: '+853', country: 'Macau', flag: '🇲🇴' },
  { code: '+886', country: 'Taiwan', flag: '🇹🇼' },
  { code: '+976', country: 'Mongolia', flag: '🇲🇳' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+7', country: 'Kazakhstan', flag: '🇰🇿' },
  { code: '+996', country: 'Kyrgyzstan', flag: '🇰🇬' },
  { code: '+992', country: 'Tajikistan', flag: '🇹🇯' },
  { code: '+993', country: 'Turkmenistan', flag: '🇹🇲' },
  { code: '+998', country: 'Uzbekistan', flag: '🇺🇿' },
  { code: '+995', country: 'Georgia', flag: '🇬🇪' },
  { code: '+994', country: 'Azerbaijan', flag: '🇦🇿' },
  { code: '+98', country: 'Iran', flag: '🇮🇷' },
  { code: '+93', country: 'Afghanistan', flag: '🇦🇫' },
  { code: '+977', country: 'Nepal', flag: '🇳🇵' },
  { code: '+975', country: 'Bhutan', flag: '🇧🇹' },
  { code: '+960', country: 'Maldives', flag: '🇲🇻' },
  
  // Middle East
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+965', country: 'Kuwait', flag: '🇰🇼' },
  { code: '+974', country: 'Qatar', flag: '🇶🇦' },
  { code: '+973', country: 'Bahrain', flag: '🇧🇭' },
  { code: '+968', country: 'Oman', flag: '🇴🇲' },
  { code: '+967', country: 'Yemen', flag: '🇾🇪' },
  { code: '+964', country: 'Iraq', flag: '🇮🇶' },
  { code: '+962', country: 'Jordan', flag: '🇯🇴' },
  { code: '+961', country: 'Lebanon', flag: '🇱🇧' },
  { code: '+963', country: 'Syria', flag: '🇸🇾' },
  { code: '+972', country: 'Israel', flag: '🇮🇱' },
  { code: '+970', country: 'Palestine', flag: '🇵🇸' },
  { code: '+90', country: 'Turkey', flag: '🇹🇷' },
  
  // Africa
  { code: '+234', country: 'Nigeria', flag: '🇳🇬' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  { code: '+20', country: 'Egypt', flag: '🇪🇬' },
  { code: '+254', country: 'Kenya', flag: '🇰🇪' },
  { code: '+256', country: 'Uganda', flag: '🇺🇬' },
  { code: '+255', country: 'Tanzania', flag: '🇹🇿' },
  { code: '+251', country: 'Ethiopia', flag: '🇪🇹' },
  { code: '+233', country: 'Ghana', flag: '🇬🇭' },
  { code: '+225', country: 'Ivory Coast', flag: '🇨🇮' },
  { code: '+221', country: 'Senegal', flag: '🇸🇳' },
  { code: '+223', country: 'Mali', flag: '🇲🇱' },
  { code: '+226', country: 'Burkina Faso', flag: '🇧🇫' },
  { code: '+227', country: 'Niger', flag: '🇳🇪' },
  { code: '+228', country: 'Togo', flag: '🇹🇬' },
  { code: '+229', country: 'Benin', flag: '🇧🇯' },
  { code: '+230', country: 'Mauritius', flag: '🇲🇺' },
  { code: '+231', country: 'Liberia', flag: '🇱🇷' },
  { code: '+232', country: 'Sierra Leone', flag: '🇸🇱' },
  { code: '+235', country: 'Chad', flag: '🇹🇩' },
  { code: '+236', country: 'Central African Republic', flag: '🇨🇫' },
  { code: '+237', country: 'Cameroon', flag: '🇨🇲' },
  { code: '+238', country: 'Cape Verde', flag: '🇨🇻' },
  { code: '+239', country: 'São Tomé and Príncipe', flag: '🇸🇹' },
  { code: '+240', country: 'Equatorial Guinea', flag: '🇬🇶' },
  { code: '+241', country: 'Gabon', flag: '🇬🇦' },
  { code: '+242', country: 'Republic of the Congo', flag: '🇨🇬' },
  { code: '+243', country: 'Democratic Republic of the Congo', flag: '🇨🇩' },
  { code: '+244', country: 'Angola', flag: '🇦🇴' },
  { code: '+245', country: 'Guinea-Bissau', flag: '🇬🇼' },
  { code: '+246', country: 'British Indian Ocean Territory', flag: '🇮🇴' },
  { code: '+248', country: 'Seychelles', flag: '🇸🇨' },
  { code: '+249', country: 'Sudan', flag: '🇸🇩' },
  { code: '+250', country: 'Rwanda', flag: '🇷🇼' },
  { code: '+252', country: 'Somalia', flag: '🇸🇴' },
  { code: '+253', country: 'Djibouti', flag: '🇩🇯' },
  { code: '+257', country: 'Burundi', flag: '🇧🇮' },
  { code: '+258', country: 'Mozambique', flag: '🇲🇿' },
  { code: '+260', country: 'Zambia', flag: '🇿🇲' },
  { code: '+261', country: 'Madagascar', flag: '🇲🇬' },
  { code: '+262', country: 'Réunion', flag: '🇷🇪' },
  { code: '+263', country: 'Zimbabwe', flag: '🇿🇼' },
  { code: '+264', country: 'Namibia', flag: '🇳🇦' },
  { code: '+265', country: 'Malawi', flag: '🇲🇼' },
  { code: '+266', country: 'Lesotho', flag: '🇱🇸' },
  { code: '+267', country: 'Botswana', flag: '🇧🇼' },
  { code: '+268', country: 'Eswatini', flag: '🇸🇿' },
  { code: '+269', country: 'Comoros', flag: '🇰🇲' },
  { code: '+290', country: 'Saint Helena', flag: '🇸🇭' },
  { code: '+291', country: 'Eritrea', flag: '🇪🇷' },
  { code: '+297', country: 'Aruba', flag: '🇦🇼' },
  { code: '+298', country: 'Faroe Islands', flag: '🇫🇴' },
  { code: '+299', country: 'Greenland', flag: '🇬🇱' },
  { code: '+212', country: 'Morocco', flag: '🇲🇦' },
  { code: '+213', country: 'Algeria', flag: '🇩🇿' },
  { code: '+216', country: 'Tunisia', flag: '🇹🇳' },
  { code: '+218', country: 'Libya', flag: '🇱🇾' },
  
  // Oceania
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
  { code: '+679', country: 'Fiji', flag: '🇫🇯' },
  { code: '+685', country: 'Samoa', flag: '🇼🇸' },
  { code: '+686', country: 'Kiribati', flag: '🇰🇮' },
  { code: '+687', country: 'New Caledonia', flag: '🇳🇨' },
  { code: '+688', country: 'Tuvalu', flag: '🇹🇻' },
  { code: '+689', country: 'French Polynesia', flag: '🇵🇫' },
  { code: '+690', country: 'Tokelau', flag: '🇹🇰' },
  { code: '+691', country: 'Micronesia', flag: '🇫🇲' },
  { code: '+692', country: 'Marshall Islands', flag: '🇲🇭' },
  { code: '+850', country: 'North Korea', flag: '🇰🇵' },
  { code: '+1242', country: 'Bahamas', flag: '🇧🇸' },
  { code: '+1246', country: 'Barbados', flag: '🇧🇧' },
  { code: '+1264', country: 'Anguilla', flag: '🇦🇮' },
  { code: '+1268', country: 'Antigua and Barbuda', flag: '🇦🇬' },
  { code: '+1284', country: 'British Virgin Islands', flag: '🇻🇬' },
  { code: '+1340', country: 'US Virgin Islands', flag: '🇻🇮' },
  { code: '+1345', country: 'Cayman Islands', flag: '🇰🇾' },
  { code: '+1441', country: 'Bermuda', flag: '🇧🇲' },
  { code: '+1473', country: 'Grenada', flag: '🇬🇩' },
  { code: '+1649', country: 'Turks and Caicos', flag: '🇹🇨' },
  { code: '+1664', country: 'Montserrat', flag: '🇲🇸' },
  { code: '+1670', country: 'Northern Mariana Islands', flag: '🇲🇵' },
  { code: '+1671', country: 'Guam', flag: '🇬🇺' },
  { code: '+1684', country: 'American Samoa', flag: '🇦🇸' },
  { code: '+1721', country: 'Sint Maarten', flag: '🇸🇽' },
  { code: '+1758', country: 'Saint Lucia', flag: '🇱🇨' },
  { code: '+1767', country: 'Dominica', flag: '🇩🇲' },
  { code: '+1784', country: 'Saint Vincent and the Grenadines', flag: '🇻🇨' },
  { code: '+1787', country: 'Puerto Rico', flag: '🇵🇷' },
  { code: '+1809', country: 'Dominican Republic', flag: '🇩🇴' },
  { code: '+1829', country: 'Dominican Republic', flag: '🇩🇴' },
  { code: '+1849', country: 'Dominican Republic', flag: '🇩🇴' },
  { code: '+1868', country: 'Trinidad and Tobago', flag: '🇹🇹' },
  { code: '+1869', country: 'Saint Kitts and Nevis', flag: '🇰🇳' },
  { code: '+1876', country: 'Jamaica', flag: '🇯🇲' },
  { code: '+1939', country: 'Puerto Rico', flag: '🇵🇷' }
];

// Sort countries alphabetically by name for better UX
const countries = [
  { code: 'AF', name: 'Afghanistan', states: [
    'Badakhshan', 'Badghis', 'Baghlan', 'Balkh', 'Bamyan', 'Daykundi', 'Farah',
    'Faryab', 'Ghazni', 'Ghor', 'Helmand', 'Herat', 'Jowzjan', 'Kabul',
    'Kandahar', 'Kapisa', 'Khost', 'Kunar', 'Kunduz', 'Laghman', 'Logar',
    'Nangarhar', 'Nimroz', 'Nuristan', 'Paktia', 'Paktika', 'Panjshir',
    'Parwan', 'Samangan', 'Sar-e Pol', 'Takhar', 'Urozgan', 'Wardak', 'Zabul'
  ]},
  { code: 'AL', name: 'Albania', states: [
    'Berat', 'Dibër', 'Durrës', 'Elbasan', 'Fier', 'Gjirokastër', 'Korçë',
    'Kukës', 'Lezhë', 'Shkodër', 'Tirana', 'Vlorë'
  ]},
  { code: 'DZ', name: 'Algeria', states: [
    'Adrar', 'Ain Defla', 'Ain Temouchent', 'Alger', 'Annaba', 'Batna', 'Bechar',
    'Bejaia', 'Biskra', 'Blida', 'Bordj Bou Arreridj', 'Bouira',
    'Boumerdes', 'Chlef', 'Constantine', 'Djelfa', 'El Bayadh', 'El Oued',
    'El Tarf', 'Ghardaia', 'Guelma', 'Illizi', 'Jijel', 'Khenchela',
    'Laghouat', 'Mascara', 'Medea', 'Mila', 'Mostaganem', 'Msila',
    'Naama', 'Oran', 'Ouargla', 'Oum el Bouaghi', 'Relizane', 'Saida',
    'Setif', 'Sidi Bel Abbes', 'Skikda', 'Souk Ahras', 'Tamanrasset',
    'Tebessa', 'Tiaret', 'Tindouf', 'Tipaza', 'Tissemsilt', 'Tizi Ouzou', 'Tlemcen'
  ]},
  { code: 'AD', name: 'Andorra', states: [
    'Andorra la Vella', 'Canillo', 'Encamp', 'Escaldes-Engordany', 'La Massana',
    'Ordino', 'Sant Julià de Lòria'
  ]},
  { code: 'AO', name: 'Angola', states: [
    'Bengo', 'Benguela', 'Bie', 'Cabinda', 'Cuando Cubango', 'Cuanza Norte',
    'Cuanza Sul', 'Cunene', 'Huambo', 'Huila', 'Luanda', 'Lunda Norte',
    'Lunda Sul', 'Malanje', 'Moxico', 'Namibe', 'Uige', 'Zaire'
  ]},
  { code: 'AG', name: 'Antigua and Barbuda', states: [
    'Barbuda', 'Redonda', 'Saint George', 'Saint John', 'Saint Mary', 'Saint Paul', 'Saint Peter', 'Saint Philip'
  ]},
  { code: 'AR', name: 'Argentina', states: [
    'Buenos Aires', 'Catamarca', 'Chaco', 'Chubut', 'Córdoba', 'Corrientes',
    'Entre Ríos', 'Formosa', 'Jujuy', 'La Pampa', 'La Rioja', 'Mendoza',
    'Misiones', 'Neuquén', 'Río Negro', 'Salta', 'San Juan', 'San Luis',
    'Santa Cruz', 'Santa Fe', 'Santiago del Estero', 'Tierra del Fuego',
    'Tucumán', 'Ciudad Autónoma de Buenos Aires'
  ]},
  { code: 'AM', name: 'Armenia', states: [
    'Aragatsotn', 'Ararat', 'Armavir', 'Gegharkunik', 'Kotayk', 'Lori',
    'Shirak', 'Syunik', 'Tavush', 'Vayots Dzor', 'Yerevan'
  ]},
  { code: 'AU', name: 'Australia', states: [
    'Australian Capital Territory', 'New South Wales', 'Northern Territory', 'Queensland',
    'South Australia', 'Tasmania', 'Victoria', 'Western Australia'
  ]},
  { code: 'AT', name: 'Austria', states: [
    'Burgenland', 'Carinthia', 'Lower Austria', 'Upper Austria', 'Salzburg', 'Styria',
    'Tyrol', 'Vorarlberg', 'Vienna'
  ]},
  { code: 'AZ', name: 'Azerbaijan', states: [
    'Absheron', 'Agdam', 'Agdash', 'Agstafa', 'Agsu', 'Astara', 'Baku', 'Balakan',
    'Barda', 'Beylagan', 'Bilasuvar', 'Dashkasan', 'Fuzuli', 'Gadabay', 'Ganja',
    'Gobustan', 'Goranboy', 'Goychay', 'Hajigabul', 'Imishli', 'Ismailli',
    'Jabrayil', 'Jalilabad', 'Kalbajar', 'Kurdamir', 'Lachin', 'Lankaran',
    'Lerik', 'Masalli', 'Mingachevir', 'Naftalan', 'Nakhchivan', 'Neftchala',
    'Oguz', 'Qabala', 'Qakh', 'Qazakh', 'Quba', 'Qubadli', 'Qusar', 'Saatli',
    'Sabirabad', 'Salyan', 'Shamakhi', 'Shamkir', 'Shirvan', 'Shusha', 'Siazan',
    'Sumgayit', 'Tartar', 'Tovuz', 'Ujar', 'Yardimli', 'Yevlakh', 'Zangilan', 'Zaqatala', 'Zardab'
  ]},

  // B
  { code: 'BS', name: 'Bahamas', states: [
    'Acklins', 'Berry Islands', 'Bimini', 'Black Point', 'Cat Island', 'Central Abaco',
    'Central Andros', 'Central Eleuthera', 'City of Freeport', 'Crooked Island and Long Cay',
    'East Grand Bahama', 'Exuma', 'Grand Cay', 'Harbour Island', 'Hope Town',
    'Inagua', 'Long Island', 'Mangrove Cay', 'Mayaguana', 'Moore\'s Island',
    'Nassau', 'New Providence', 'North Abaco', 'North Andros', 'North Eleuthera',
    'Ragged Island', 'Rum Cay', 'San Salvador', 'South Abaco', 'South Andros',
    'South Eleuthera', 'Spanish Wells', 'West Grand Bahama'
  ]},
  { code: 'BH', name: 'Bahrain', states: [
    'Capital', 'Central', 'Muharraq', 'Northern', 'Southern'
  ]},
  { code: 'BD', name: 'Bangladesh', states: [
    'Barisal', 'Chittagong', 'Dhaka', 'Khulna', 'Mymensingh', 'Rajshahi', 'Rangpur', 'Sylhet'
  ]},
  { code: 'BB', name: 'Barbados', states: [
    'Christ Church', 'Saint Andrew', 'Saint George', 'Saint James', 'Saint John',
    'Saint Joseph', 'Saint Lucy', 'Saint Michael', 'Saint Peter', 'Saint Philip', 'Saint Thomas'
  ]},
  { code: 'BY', name: 'Belarus', states: [
    'Brest', 'Gomel', 'Grodno', 'Minsk', 'Mogilev', 'Vitebsk', 'Minsk City'
  ]},
  { code: 'BE', name: 'Belgium', states: [
    'Antwerp', 'Brussels', 'East Flanders', 'Flemish Brabant', 'Hainaut', 'Liège',
    'Limburg', 'Luxembourg', 'Namur', 'Walloon Brabant', 'West Flanders'
  ]},
  { code: 'BZ', name: 'Belize', states: [
    'Belize', 'Cayo', 'Corozal', 'Orange Walk', 'Stann Creek', 'Toledo'
  ]},
  { code: 'BJ', name: 'Benin', states: [
    'Alibori', 'Atakora', 'Atlantique', 'Borgou', 'Collines', 'Couffo',
    'Donga', 'Littoral', 'Mono', 'Oueme', 'Plateau', 'Zou'
  ]},
  { code: 'BT', name: 'Bhutan', states: [
    'Bumthang', 'Chhukha', 'Dagana', 'Gasa', 'Haa', 'Lhuentse', 'Mongar',
    'Paro', 'Pemagatshel', 'Punakha', 'Samdrup Jongkhar', 'Samtse',
    'Sarpang', 'Thimphu', 'Trashigang', 'Trashiyangtse', 'Trongsa',
    'Tsirang', 'Wangdue Phodrang', 'Zhemgang'
  ]},
  { code: 'BO', name: 'Bolivia', states: [
    'Beni', 'Chuquisaca', 'Cochabamba', 'La Paz', 'Oruro', 'Pando', 'Potosi',
    'Santa Cruz', 'Tarija'
  ]},
  { code: 'BA', name: 'Bosnia and Herzegovina', states: [
    'Brčko District', 'Federation of Bosnia and Herzegovina', 'Republika Srpska'
  ]},
  { code: 'BW', name: 'Botswana', states: [
    'Central', 'Ghanzi', 'Kgalagadi', 'Kgatleng', 'Kweneng', 'North East',
    'North West', 'South East', 'Southern'
  ]},
  { code: 'BR', name: 'Brazil', states: [
    'Acre', 'Alagoas', 'Amapá', 'Amazonas', 'Bahia', 'Ceará', 'Distrito Federal',
    'Espírito Santo', 'Goiás', 'Maranhão', 'Mato Grosso', 'Mato Grosso do Sul',
    'Minas Gerais', 'Pará', 'Paraíba', 'Paraná', 'Pernambuco', 'Piauí',
    'Rio de Janeiro', 'Rio Grande do Norte', 'Rio Grande do Sul', 'Rondônia',
    'Roraima', 'Santa Catarina', 'São Paulo', 'Sergipe', 'Tocantins'
  ]},
  { code: 'BN', name: 'Brunei', states: [
    'Belait', 'Brunei-Muara', 'Temburong', 'Tutong'
  ]},
  { code: 'BG', name: 'Bulgaria', states: [
    'Blagoevgrad', 'Burgas', 'Dobrich', 'Gabrovo', 'Haskovo', 'Kardzhali',
    'Kyustendil', 'Lovech', 'Montana', 'Pazardzhik', 'Pernik', 'Pleven',
    'Plovdiv', 'Razgrad', 'Ruse', 'Shumen', 'Silistra', 'Sliven', 'Smolyan',
    'Sofia', 'Sofia City', 'Stara Zagora', 'Targovishte', 'Varna', 'Veliko Tarnovo',
    'Vidin', 'Vratsa', 'Yambol'
  ]},
  { code: 'BF', name: 'Burkina Faso', states: [
    'Bale', 'Bam', 'Banwa', 'Bazega', 'Bougouriba', 'Boulgou', 'Boulkiemde',
    'Comoe', 'Ganzourgou', 'Gnagna', 'Gourma', 'Houet', 'Ioba', 'Kadiogo',
    'Kenedougou', 'Komondjari', 'Kompienga', 'Kossi', 'Koulpelogo', 'Kouritenga',
    'Kourweogo', 'Leraba', 'Loroum', 'Mouhoun', 'Nahouri', 'Namentenga',
    'Nayala', 'Noumbiel', 'Oubritenga', 'Oudalan', 'Passore', 'Poni',
    'Sanguie', 'Sanmatenga', 'Seno', 'Sissili', 'Soum', 'Sourou', 'Tapoa',
    'Tuy', 'Yagha', 'Yatenga', 'Ziro', 'Zondoma', 'Zoundweogo'
  ]},
  { code: 'BI', name: 'Burundi', states: [
    'Bubanza', 'Bujumbura Mairie', 'Bujumbura Rural', 'Bururi', 'Cankuzo',
    'Cibitoke', 'Gitega', 'Karuzi', 'Kayanza', 'Kirundo', 'Makamba',
    'Muramvya', 'Muyinga', 'Mwaro', 'Ngozi', 'Rumonge', 'Rutana', 'Ruyigi'
  ]},
  // C
  { code: 'KH', name: 'Cambodia', states: [
    'Banteay Meanchey', 'Battambang', 'Kampong Cham', 'Kampong Chhnang',
    'Kampong Speu', 'Kampong Thom', 'Kampot', 'Kandal', 'Kep', 'Koh Kong',
    'Kratie', 'Mondulkiri', 'Oddar Meanchey', 'Pailin', 'Phnom Penh',
    'Preah Vihear', 'Prey Veng', 'Pursat', 'Ratanakiri', 'Siem Reap',
    'Sihanoukville', 'Stung Treng', 'Svay Rieng', 'Takeo', 'Tbong Khmum'
  ]},
  { code: 'CM', name: 'Cameroon', states: [
    'Adamawa', 'Centre', 'East', 'Far North', 'Littoral', 'North',
    'Northwest', 'South', 'Southwest', 'West'
  ]},
  { code: 'CA', name: 'Canada', states: [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
    'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
    'Quebec', 'Saskatchewan', 'Yukon'
  ]},
  { code: 'CV', name: 'Cape Verde', states: [
    'Boa Vista', 'Brava', 'Fogo', 'Maio', 'Sal', 'Santiago',
    'Santo Antão', 'São Nicolau', 'São Vicente'
  ]},
  { code: 'CF', name: 'Central African Republic', states: [
    'Bamingui-Bangoran', 'Bangui', 'Basse-Kotto', 'Haute-Kotto', 'Haut-Mbomou',
    'Kémo', 'Lobaye', 'Mambéré-Kadéï', 'Mbomou', 'Nana-Grébizi', 'Nana-Mambéré',
    'Ombella-M\'Poko', 'Ouaka', 'Ouham', 'Ouham-Pendé', 'Sangha-Mbaéré', 'Vakaga'
  ]},
  { code: 'TD', name: 'Chad', states: [
    'Bahr el Gazel', 'Batha', 'Borkou', 'Chari-Baguirmi', 'Ennedi-Est', 'Ennedi-Ouest',
    'Guéra', 'Hadjer-Lamis', 'Kanem', 'Lac', 'Logone Occidental', 'Logone Oriental',
    'Mandoul', 'Mayo-Kebbi Est', 'Mayo-Kebbi Ouest', 'Moyen-Chari', 'N\'Djamena',
    'Ouaddaï', 'Salamat', 'Sila', 'Tandjilé', 'Tibesti', 'Wadi Fira'
  ]},
  { code: 'CL', name: 'Chile', states: [
    'Aisen', 'Antofagasta', 'Araucania', 'Arica y Parinacota', 'Atacama', 'Bio-Bio',
    'Coquimbo', 'Los Lagos', 'Los Rios', 'Magallanes', 'Maule', 'Metropolitan',
    'O\'Higgins', 'Tarapaca', 'Valparaiso'
  ]},
  { code: 'CN', name: 'China', states: [
    'Anhui', 'Beijing', 'Chongqing', 'Fujian', 'Gansu', 'Guangdong', 'Guangxi',
    'Guizhou', 'Hainan', 'Hebei', 'Heilongjiang', 'Henan', 'Hubei',
    'Hunan', 'Inner Mongolia', 'Jiangsu', 'Jiangxi', 'Jilin', 'Liaoning',
    'Ningxia', 'Qinghai', 'Shaanxi', 'Shandong', 'Shanghai', 'Shanxi', 'Sichuan',
    'Tianjin', 'Tibet', 'Xinjiang', 'Yunnan', 'Zhejiang'
  ]},
  { code: 'CO', name: 'Colombia', states: [
    'Amazonas', 'Antioquia', 'Arauca', 'Atlantico', 'Bogota', 'Bolivar', 'Boyaca',
    'Caldas', 'Caqueta', 'Casanare', 'Cauca', 'Cesar', 'Choco', 'Cordoba',
    'Cundinamarca', 'Guainia', 'Guaviare', 'Huila', 'La Guajira', 'Magdalena',
    'Meta', 'Narino', 'Norte de Santander', 'Putumayo', 'Quindio', 'Risaralda',
    'San Andres y Providencia', 'Santander', 'Sucre', 'Tolima', 'Valle del Cauca',
    'Vaupes', 'Vichada'
  ]},
  { code: 'KM', name: 'Comoros', states: [
    'Anjouan', 'Grande Comore', 'Mohéli'
  ]},
  { code: 'CG', name: 'Congo', states: [
    'Bouenza', 'Brazzaville', 'Cuvette', 'Cuvette-Ouest', 'Kouilou',
    'Lékoumou', 'Likouala', 'Niari', 'Plateaux', 'Pointe-Noire', 'Pool', 'Sangha'
  ]},
  { code: 'CD', name: 'Congo (Democratic Republic)', states: [
    'Bas-Uele', 'Équateur', 'Haut-Katanga', 'Haut-Lomami', 'Haut-Uele', 'Ituri',
    'Kasaï', 'Kasaï-Central', 'Kasaï-Oriental', 'Kinshasa', 'Kongo-Central',
    'Kwango', 'Kwilu', 'Lomami', 'Lualaba', 'Mai-Ndombe', 'Maniema', 'Mongala',
    'Nord-Kivu', 'Nord-Ubangi', 'Sankuru', 'Sud-Kivu', 'Sud-Ubangi', 'Tanganyika',
    'Tshopo', 'Tshuapa'
  ]},
  { code: 'CR', name: 'Costa Rica', states: [
    'Alajuela', 'Cartago', 'Guanacaste', 'Heredia', 'Limon', 'Puntarenas', 'San Jose'
  ]},
  { code: 'CI', name: 'Côte d\'Ivoire', states: [
    'Abidjan', 'Agnéby-Tiassa', 'Bafing', 'Bagoué', 'Béré', 'Bounkani',
    'Cavally', 'Folon', 'Gbêkê', 'Gbôklé', 'Gôh', 'Gontougo', 'Grands-Ponts',
    'Guémon', 'Hambol', 'Haut-Sassandra', 'Iffou', 'Indénié-Djuablin',
    'Kabadougou', 'Lôh-Djiboua', 'Marahoué', 'Moronou', 'Nawa', 'N\'zi',
    'Poro', 'San-Pédro', 'Tchologo', 'Tonkpi', 'Worodougou', 'Yamoussoukro'
  ]},
  { code: 'HR', name: 'Croatia', states: [
    'Bjelovar-Bilogora', 'Brodsko-posavska', 'Dubrovnik-Neretva', 'Grad Zagreb',
    'Istria', 'Karlovac', 'Koprivnica-Križevci', 'Krapina-Zagorje', 'Lika-Senj',
    'Međimurje', 'Osijek-Baranja', 'Požega-Slavonia', 'Primorje-Gorski Kotar',
    'Šibenik-Knin', 'Sisak-Moslavina', 'Split-Dalmatia', 'Varaždin',
    'Virovitica-Podravina', 'Vukovar-Srijem', 'Zadar', 'Zagreb'
  ]},
  { code: 'CU', name: 'Cuba', states: [
    'Artemisa', 'Camagüey', 'Ciego de Ávila', 'Cienfuegos', 'Granma', 'Guantánamo',
    'Havana', 'Holguín', 'Isle of Youth', 'Las Tunas', 'Matanzas', 'Mayabeque',
    'Pinar del Río', 'Sancti Spíritus', 'Santiago de Cuba', 'Villa Clara'
  ]},
  { code: 'CY', name: 'Cyprus', states: [
    'Famagusta', 'Kyrenia', 'Larnaca', 'Limassol', 'Nicosia', 'Paphos'
  ]},
  { code: 'CZ', name: 'Czech Republic', states: [
    'Prague', 'Central Bohemian', 'South Bohemian', 'Plzen', 'Karlovy Vary',
    'Usti nad Labem', 'Liberec', 'Hradec Kralove', 'Pardubice', 'Vysocina',
    'South Moravian', 'Olomouc', 'Zlin', 'Moravian-Silesian'
  ]},
  
  // D
  { code: 'DK', name: 'Denmark', states: [
    'Capital Region', 'Central Denmark', 'North Denmark', 'Region Zealand', 'Southern Denmark'
  ]},
  { code: 'DJ', name: 'Djibouti', states: [
    'Ali Sabieh', 'Arta', 'Dikhil', 'Djibouti', 'Obock', 'Tadjourah'
  ]},
  { code: 'DM', name: 'Dominica', states: [
    'Saint Andrew', 'Saint David', 'Saint George', 'Saint John', 'Saint Joseph',
    'Saint Luke', 'Saint Mark', 'Saint Patrick', 'Saint Paul', 'Saint Peter'
  ]},
  { code: 'DO', name: 'Dominican Republic', states: [
    'Azua', 'Baoruco', 'Barahona', 'Dajabón', 'Distrito Nacional', 'Duarte',
    'El Seibo', 'Elías Piña', 'Espaillat', 'Hato Mayor', 'Hermanas Mirabal',
    'Independencia', 'La Altagracia', 'La Romana', 'La Vega', 'María Trinidad Sánchez',
    'Monseñor Nouel', 'Monte Cristi', 'Monte Plata', 'Pedernales', 'Peravia',
    'Puerto Plata', 'Samaná', 'San Cristóbal', 'San José de Ocoa', 'San Juan',
    'San Pedro de Macorís', 'Sánchez Ramírez', 'Santiago', 'Santiago Rodríguez',
    'Santo Domingo', 'Valverde'
  ]},

  // E
  { code: 'EG', name: 'Egypt', states: [
    'Alexandria', 'Aswan', 'Asyut', 'Beheira', 'Beni Suef', 'Cairo', 'Dakahlia',
    'Damietta', 'Faiyum', 'Gharbia', 'Giza', 'Ismailia', 'Kafr el-Sheikh',
    'Luxor', 'Matrouh', 'Minya', 'Monufia', 'New Valley', 'North Sinai',
    'Port Said', 'Qalyubia', 'Qena', 'Red Sea', 'Sharqia', 'Sohag', 'South Sinai', 'Suez'
  ]},
  { code: 'ER', name: 'Eritrea', states: [
    'Anseba', 'Debub', 'Debubawi Keyih Bahri', 'Gash-Barka', 'Maekel', 'Semenawi Keyih Bahri'
  ]},
  { code: 'ET', name: 'Ethiopia', states: [
    'Addis Ababa', 'Afar', 'Amhara', 'Benishangul-Gumuz', 'Dire Dawa', 'Gambela',
    'Harari', 'Oromia', 'Sidama', 'Somali', 'Southern Nations', 'Tigray'
  ]},

  // F
  { code: 'FI', name: 'Finland', states: [
    'Åland', 'Central Finland', 'Central Ostrobothia', 'Finland Proper', 'Kainuu',
    'Kanta-Häme', 'Karelia', 'Kymenlaakso', 'Lapland', 'North Karelia', 'Northern Ostrobothia',
    'Northern Savonia', 'Ostrobothnia', 'Päijät-Häme', 'Pirkanmaa', 'Satakunta',
    'South Karelia', 'Southern Ostrobothnia', 'Southern Savonia', 'Uusimaa'
  ]},
  { code: 'FR', name: 'France', states: [
    'Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté', 'Brittany', 'Centre-Val de Loire',
    'Corsica', 'Grand Est', 'Hauts-de-France', 'Île-de-France', 'Normandy', 'Nouvelle-Aquitaine',
    'Occitanie', 'Pays de la Loire', 'Provence-Alpes-Côte d\'Azur'
  ]},

  // G
  { code: 'GE', name: 'Georgia', states: [
    'Adjara', 'Guria', 'Imereti', 'Kakheti', 'Kvemo Kartli', 'Mtskheta-Mtianeti',
    'Racha-Lechkhumi', 'Samegrelo-Zemo Svaneti', 'Samtskhe-Javakheti', 'Shida Kartli',
    'Tbilisi', 'Abkhazia', 'South Ossetia'
  ]},
  { code: 'DE', name: 'Germany', states: [
    'Baden-Württemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg',
    'Hesse', 'Lower Saxony', 'Mecklenburg-Vorpommern', 'North Rhine-Westphalia',
    'Rhineland-Palatinate', 'Saarland', 'Saxony', 'Saxony-Anhalt', 'Schleswig-Holstein',
    'Thuringia'
  ]},
  { code: 'GH', name: 'Ghana', states: [
    'Ahafo', 'Ashanti', 'Bono', 'Bono East', 'Central', 'Eastern', 'Greater Accra',
    'North East', 'Northern', 'Oti', 'Savannah', 'Upper East', 'Upper West',
    'Volta', 'Western', 'Western North'
  ]},
  { code: 'GR', name: 'Greece', states: [
    'Attica', 'Central Greece', 'Central Macedonia', 'Crete', 'Eastern Macedonia and Thrace',
    'Epirus', 'Ionian Islands', 'North Aegean', 'Peloponnese', 'South Aegean',
    'Thessaly', 'Western Greece', 'Western Macedonia'
  ]},

  // H-I
  { code: 'HU', name: 'Hungary', states: [
    'Bacs-Kiskun', 'Baranya', 'Bekes', 'Borsod-Abauj-Zemplen', 'Budapest',
    'Csongrad-Csanad', 'Fejer', 'Gyor-Moson-Sopron', 'Hajdu-Bihar', 'Heves',
    'Jasz-Nagykun-Szolnok', 'Komarom-Esztergom', 'Nograd', 'Pest', 'Somogy',
    'Szabolcs-Szatmar-Bereg', 'Tolna', 'Vas', 'Veszprem', 'Zala'
  ]},
  { code: 'IS', name: 'Iceland', states: [
    'Capital Region', 'Southern Peninsula', 'Western Region', 'Westfjords',
    'Northwestern Region', 'Northeastern Region', 'Eastern Region', 'Southern Region'
  ]},
  { code: 'IN', name: 'India', states: [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
    'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
    'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
    'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Andaman and Nicobar Islands',
    'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir',
    'Ladakh', 'Lakshadweep', 'Puducherry'
  ]},
  { code: 'ID', name: 'Indonesia', states: [
    'Aceh', 'Bali', 'Bangka Belitung', 'Banten', 'Bengkulu', 'Central Java',
    'Central Kalimantan', 'Central Sulawesi', 'East Java', 'East Kalimantan',
    'East Nusa Tenggara', 'Gorontalo', 'Jakarta', 'Jambi', 'Lampung', 'Maluku',
    'North Kalimantan', 'North Maluku', 'North Sulawesi', 'North Sumatra',
    'Papua', 'Riau', 'Riau Islands', 'South Kalimantan', 'South Sulawesi',
    'South Sumatra', 'Southeast Sulawesi', 'West Java', 'West Kalimantan',
    'West Nusa Tenggara', 'West Papua', 'West Sulawesi', 'West Sumatra', 'Yogyakarta'
  ]},
  { code: 'IR', name: 'Iran', states: [
    'Alborz', 'Ardabil', 'Azerbaijan East', 'Azerbaijan West', 'Bushehr', 'Chaharmahal and Bakhtiari',
    'Fars', 'Gilan', 'Golestan', 'Hamadan', 'Hormozgan', 'Ilam', 'Isfahan', 'Kerman',
    'Kermanshah', 'Khuzestan', 'Kohgiluyeh and Boyer-Ahmad', 'Kurdistan', 'Lorestan',
    'Markazi', 'Mazandaran', 'North Khorasan', 'Qazvin', 'Qom', 'Razavi Khorasan',
    'Semnan', 'Sistan and Baluchestan', 'South Khorasan', 'Tehran', 'Yazd', 'Zanjan'
  ]},
  { code: 'IQ', name: 'Iraq', states: [
    'Al Anbar', 'Al Basrah', 'Al Muthanna', 'Al Qadisiyyah', 'An Najaf', 'Arbil',
    'As Sulaymaniyyah', 'Babil', 'Baghdad', 'Dahuk', 'Dhi Qar', 'Diyala',
    'Karbala', 'Kirkuk', 'Maysan', 'Ninawa', 'Salah ad Din', 'Wasit'
  ]},
  { code: 'IE', name: 'Ireland', states: [
    'Carlow', 'Cavan', 'Clare', 'Cork', 'Donegal', 'Dublin', 'Galway', 'Kerry',
    'Kildare', 'Kilkenny', 'Laois', 'Leitrim', 'Limerick', 'Longford', 'Louth',
    'Mayo', 'Meath', 'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary',
    'Waterford', 'Westmeath', 'Wexford', 'Wicklow'
  ]},
  { code: 'IL', name: 'Israel', states: [
    'Central', 'Haifa', 'Jerusalem', 'Northern', 'Southern', 'Tel Aviv'
  ]},
  { code: 'IT', name: 'Italy', states: [
    'Abruzzo', 'Basilicata', 'Calabria', 'Campania', 'Emilia-Romagna', 'Friuli-Venezia Giulia',
    'Lazio', 'Liguria', 'Lombardy', 'Marche', 'Molise', 'Piedmont', 'Puglia', 'Sardinia',
    'Sicily', 'Trentino-Alto Adige', 'Tuscany', 'Umbria', 'Valle d\'Aosta', 'Veneto'
  ]},

  // J
  { code: 'JP', name: 'Japan', states: [
    'Aichi', 'Akita', 'Aomori', 'Chiba', 'Ehime', 'Fukui', 'Fukuoka', 'Fukushima',
    'Gifu', 'Gunma', 'Hiroshima', 'Hokkaido', 'Hyogo', 'Ibaraki', 'Ishikawa',
    'Iwate', 'Kagawa', 'Kagoshima', 'Kanagawa', 'Kochi', 'Kumamoto', 'Kyoto',
    'Mie', 'Miyagi', 'Miyazaki', 'Nagano', 'Nagasaki', 'Nara', 'Niigata',
    'Oita', 'Okayama', 'Okinawa', 'Osaka', 'Saga', 'Saitama', 'Shiga',
    'Shimane', 'Shizuoka', 'Tochigi', 'Tokushima', 'Tokyo', 'Tottori',
    'Toyama', 'Wakayama', 'Yamagata', 'Yamaguchi', 'Yamanashi'
  ]},
  { code: 'JO', name: 'Jordan', states: [
    'Ajloun', 'Amman', 'Aqaba', 'Balqa', 'Irbid', 'Jarash', 'Karak', 'Ma\'an',
    'Madaba', 'Mafraq', 'Tafilah', 'Zarqa'
  ]},

  // K-L
  { code: 'KE', name: 'Kenya', states: [
    'Baringo', 'Bomet', 'Bungoma', 'Busia', 'Elgeyo-Marakwet', 'Embu', 'Garissa',
    'Homa Bay', 'Isiolo', 'Kajiado', 'Kakamega', 'Kericho', 'Kiambu', 'Kilifi',
    'Kirinyaga', 'Kisii', 'Kisumu', 'Kitui', 'Kwale', 'Laikipia', 'Lamu',
    'Machakos', 'Makueni', 'Mandera', 'Marsabit', 'Meru', 'Migori', 'Mombasa',
    'Murang\'a', 'Nairobi', 'Nakuru', 'Nandi', 'Narok', 'Nyamira', 'Nyandarua',
    'Nyeri', 'Samburu', 'Siaya', 'Taita-Taveta', 'Tana River', 'Tharaka-Nithi',
    'Trans Nzoia', 'Turkana', 'Uasin Gishu', 'Vihiga', 'Wajir', 'West Pokot'
  ]},
  { code: 'KR', name: 'South Korea', states: [
    'Busan', 'Chungcheongbuk-do', 'Chungcheongnam-do', 'Daegu', 'Daejeon',
    'Gangwon-do', 'Gwangju', 'Gyeonggi-do', 'Gyeongsangbuk-do', 'Gyeongsangnam-do',
    'Incheon', 'Jeju', 'Jeollabuk-do', 'Jeollanam-do', 'Sejong', 'Seoul', 'Ulsan'
  ]},
  { code: 'KW', name: 'Kuwait', states: [
    'Al Ahmadi', 'Al Farwaniyah', 'Al Jahra', 'Capital', 'Hawalli', 'Mubarak Al-Kabeer'
  ]},
  { code: 'LB', name: 'Lebanon', states: [
    'Akkar', 'Baalbek-Hermel', 'Beirut', 'Beqaa', 'Mount Lebanon', 'Nabatieh', 'North', 'South'
  ]},
  { code: 'LT', name: 'Lithuania', states: [
    'Alytus', 'Kaunas', 'Klaipeda', 'Marijampole', 'Panevezys', 'Siauliai',
    'Taurage', 'Telsiai', 'Utena', 'Vilnius'
  ]},

  // M
  { code: 'MY', name: 'Malaysia', states: [
    'Johor', 'Kedah', 'Kelantan', 'Kuala Lumpur', 'Labuan', 'Malacca', 'Negeri Sembilan',
    'Pahang', 'Penang', 'Perak', 'Perlis', 'Putrajaya', 'Sabah', 'Sarawak', 'Selangor', 'Terengganu'
  ]},
  { code: 'MX', name: 'Mexico', states: [
    'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas', 'Chihuahua',
    'Coahuila', 'Colima', 'Durango', 'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Mexico',
    'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro',
    'Quintana Roo', 'San Luis Potosí', 'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas',
    'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas', 'Mexico City'
  ]},
  { code: 'MA', name: 'Morocco', states: [
    'Béni Mellal-Khénifra', 'Casablanca-Settat', 'Dakhla-Oued Ed-Dahab', 'Drâa-Tafilalet',
    'Fès-Meknès', 'Guelmim-Oued Noun', 'Laâyoune-Sakia El Hamra', 'Marrakech-Safi',
    'Oriental', 'Rabat-Salé-Kénitra', 'Souss-Massa', 'Tanger-Tétouan-Al Hoceïma'
  ]},

  // N
  { code: 'NL', name: 'Netherlands', states: [
    'Drenthe', 'Flevoland', 'Friesland', 'Gelderland', 'Groningen', 'Limburg',
    'North Brabant', 'North Holland', 'Overijssel', 'South Holland', 'Utrecht', 'Zeeland'
  ]},
  { code: 'NZ', name: 'New Zealand', states: [
    'Auckland', 'Bay of Plenty', 'Canterbury', 'Gisborne', 'Hawke\'s Bay', 'Manawatu-Wanganui',
    'Marlborough', 'Nelson', 'Northland', 'Otago', 'Southland', 'Taranaki', 'Tasman',
    'Waikato', 'Wellington', 'West Coast'
  ]},
  { code: 'NG', name: 'Nigeria', states: [
    'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
    'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'Federal Capital Territory',
    'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara',
    'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers',
    'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
  ]},
  { code: 'NO', name: 'Norway', states: [
    'Agder', 'Innlandet', 'Møre og Romsdal', 'Nordland', 'Oslo', 'Rogaland',
    'Troms og Finnmark', 'Trøndelag', 'Vestfold og Telemark', 'Vestland', 'Viken'
  ]},

  // P
  { code: 'PK', name: 'Pakistan', states: [
    'Balochistan', 'Gilgit-Baltistan', 'Islamabad Capital Territory', 'Khyber Pakhtunkhwa',
    'Punjab', 'Sindh'
  ]},
  { code: 'PH', name: 'Philippines', states: [
    'Abra', 'Agusan del Norte', 'Agusan del Sur', 'Aklan', 'Albay', 'Antique',
    'Apayao', 'Aurora', 'Basilan', 'Bataan', 'Batanes', 'Batangas', 'Benguet',
    'Biliran', 'Bohol', 'Bukidnon', 'Bulacan', 'Cagayan', 'Camarines Norte',
    'Camarines Sur', 'Camiguin', 'Capiz', 'Catanduanes', 'Cavite', 'Cebu',
    'Compostela Valley', 'Cotabato', 'Davao del Norte', 'Davao del Sur',
    'Davao Occidental', 'Davao Oriental', 'Dinagat Islands', 'Eastern Samar',
    'Guimaras', 'Ifugao', 'Ilocos Norte', 'Ilocos Sur', 'Iloilo', 'Isabela',
    'Kalinga', 'La Union', 'Laguna', 'Lanao del Norte', 'Lanao del Sur',
    'Leyte', 'Maguindanao', 'Marinduque', 'Masbate', 'Metro Manila',
    'Misamis Occidental', 'Misamis Oriental', 'Mountain Province', 'Negros Occidental',
    'Negros Oriental', 'Northern Samar', 'Nueva Ecija', 'Nueva Vizcaya',
    'Occidental Mindoro', 'Oriental Mindoro', 'Palawan', 'Pampanga', 'Pangasinan',
    'Quezon', 'Quirino', 'Rizal', 'Romblon', 'Samar', 'Sarangani',
    'Siquijor', 'Sorsogon', 'South Cotabato', 'Southern Leyte', 'Sultan Kudarat',
    'Sulu', 'Surigao del Norte', 'Surigao del Sur', 'Tarlac', 'Tawi-Tawi',
    'Zambales', 'Zamboanga del Norte', 'Zamboanga del Sur', 'Zamboanga Sibugay'
  ]},
  { code: 'PL', name: 'Poland', states: [
    'Greater Poland', 'Kuyavian-Pomeranian', 'Lesser Poland', 'Lodz', 'Lower Silesian',
    'Lublin', 'Lubusz', 'Masovian', 'Opole', 'Podlaskie', 'Pomeranian', 'Silesian',
    'Subcarpathian', 'Swietokrzyskie', 'Warmian-Masurian', 'West Pomeranian'
  ]},

  // Q-R
  { code: 'QA', name: 'Qatar', states: [
    'Ad Dawhah', 'Al Khor', 'Al Rayyan', 'Al Wakrah', 'Ash Shamal', 'Az Za\'ayin', 'Umm Salal'
  ]},
  { code: 'RU', name: 'Russia', states: [
    'Adygea', 'Altai Krai', 'Altai Republic', 'Amur Oblast', 'Arkhangelsk Oblast',
    'Astrakhan Oblast', 'Bashkortostan', 'Belgorod Oblast', 'Bryansk Oblast',
    'Buryatia', 'Chechen Republic', 'Chelyabinsk Oblast', 'Chukotka',
    'Chuvashia', 'Dagestan', 'Ingushetia', 'Irkutsk Oblast', 'Ivanovo Oblast',
    'Jewish Autonomous Oblast', 'Kabardino-Balkaria', 'Kaliningrad Oblast',
    'Kalmykia', 'Kaluga Oblast', 'Kamchatka Krai', 'Karachay-Cherkessia',
    'Karelia', 'Kemerovo Oblast', 'Khabarovsk Krai', 'Khakassia',
    'Khanty-Mansi', 'Kirov Oblast', 'Komi', 'Kostroma Oblast', 'Krasnodar Krai',
    'Krasnoyarsk Krai', 'Kurgan Oblast', 'Kursk Oblast', 'Leningrad Oblast',
    'Lipetsk Oblast', 'Magadan Oblast', 'Mari El', 'Mordovia', 'Moscow',
    'Moscow Oblast', 'Murmansk Oblast', 'Nenets', 'Nizhny Novgorod Oblast',
    'North Ossetia', 'Novgorod Oblast', 'Novosibirsk Oblast', 'Omsk Oblast',
    'Orenburg Oblast', 'Oryol Oblast', 'Penza Oblast', 'Perm Krai',
    'Primorsky Krai', 'Pskov Oblast', 'Rostov Oblast', 'Ryazan Oblast',
    'Saint Petersburg', 'Sakha Republic', 'Sakhalin Oblast', 'Samara Oblast',
    'Saratov Oblast', 'Smolensk Oblast', 'Stavropol Krai', 'Sverdlovsk Oblast',
    'Tambov Oblast', 'Tatarstan', 'Tomsk Oblast', 'Tula Oblast', 'Tuva',
    'Tver Oblast', 'Tyumen Oblast', 'Udmurtia', 'Ulyanovsk Oblast',
    'Vladimir Oblast', 'Volgograd Oblast', 'Vologda Oblast', 'Voronezh Oblast',
    'Yamalo-Nenets', 'Yaroslavl Oblast', 'Zabaykalsky Krai'
  ]},

  // S
  { code: 'SA', name: 'Saudi Arabia', states: [
    'Al Bahah', 'Al Hudud ash Shamaliyah', 'Al Jawf', 'Al Madinah al Munawwarah',
    'Al Qasim', 'Ar Riyad', 'Ash Sharqiyah', 'Asir', 'Ha\'il', 'Jazan',
    'Makkah al Mukarramah', 'Najran', 'Tabuk'
  ]},
  { code: 'SG', name: 'Singapore', states: [
    'Central Region', 'East Region', 'North Region', 'Northeast Region', 'West Region'
  ]},
  { code: 'ZA', name: 'South Africa', states: [
    'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
    'Mpumalanga', 'Northern Cape', 'North West', 'Western Cape'
  ]},
  { code: 'ES', name: 'Spain', states: [
    'Andalusia', 'Aragon', 'Asturias', 'Balearic Islands', 'Basque Country', 'Canary Islands',
    'Cantabria', 'Castile and León', 'Castile-La Mancha', 'Catalonia', 'Extremadura',
    'Galicia', 'La Rioja', 'Madrid', 'Murcia', 'Navarre', 'Valencia'
  ]},
  { code: 'LK', name: 'Sri Lanka', states: [
    'Central', 'Eastern', 'North Central', 'Northern', 'North Western',
    'Sabaragamuwa', 'Southern', 'Uva', 'Western'
  ]},
  { code: 'SE', name: 'Sweden', states: [
    'Blekinge', 'Dalarna', 'Gävleborg', 'Gotland', 'Halland', 'Jämtland', 'Jönköping',
    'Kalmar', 'Kronoberg', 'Norrbotten', 'Örebro', 'Östergötland', 'Skåne', 'Södermanland',
    'Stockholm', 'Uppsala', 'Värmland', 'Västerbotten', 'Västernorrland', 'Västmanland',
    'Västra Götaland'
  ]},
  { code: 'CH', name: 'Switzerland', states: [
    'Aargau', 'Appenzell Ausserrhoden', 'Appenzell Innerrhoden', 'Basel-Landschaft',
    'Basel-Stadt', 'Bern', 'Fribourg', 'Geneva', 'Glarus', 'Graubünden', 'Jura',
    'Lucerne', 'Neuchâtel', 'Nidwalden', 'Obwalden', 'Schaffhausen', 'Schwyz',
    'Solothurn', 'St. Gallen', 'Thurgau', 'Ticino', 'Uri', 'Valais', 'Vaud', 'Zug', 'Zurich'
  ]},

  // T
  { code: 'TW', name: 'Taiwan', states: [
    'Changhua', 'Chiayi', 'Hsinchu', 'Hualien', 'Kaohsiung', 'Keelung', 'Kinmen',
    'Lienchiang', 'Miaoli', 'Nantou', 'New Taipei', 'Penghu', 'Pingtung', 'Taichung',
    'Tainan', 'Taipei', 'Taitung', 'Taoyuan', 'Yilan', 'Yunlin'
  ]},
  { code: 'TH', name: 'Thailand', states: [
    'Amnat Charoen', 'Ang Thong', 'Bangkok', 'Bueng Kan', 'Buri Ram', 'Chachoengsao',
    'Chai Nat', 'Chaiyaphum', 'Chanthaburi', 'Chiang Mai', 'Chiang Rai', 'Chon Buri',
    'Chumphon', 'Kalasin', 'Kamphaeng Phet', 'Kanchanaburi', 'Khon Kaen', 'Krabi',
    'Lampang', 'Lamphun', 'Loei', 'Lop Buri', 'Mae Hong Son', 'Maha Sarakham',
    'Mukdahan', 'Nakhon Nayok', 'Nakhon Pathom', 'Nakhon Phanom', 'Nakhon Ratchasima',
    'Nakhon Sawan', 'Nakhon Si Thammarat', 'Nan', 'Narathiwat', 'Nong Bua Lam Phu',
    'Nong Khai', 'Nonthaburi', 'Pathum Thani', 'Pattani', 'Phang Nga', 'Phatthalung',
    'Phayao', 'Phetchabun', 'Phetchaburi', 'Phichit', 'Phitsanulok', 'Phra Nakhon Si Ayutthaya',
    'Phrae', 'Phuket', 'Prachin Buri', 'Prachuap Khiri Khan', 'Ranong', 'Ratchaburi',
    'Rayong', 'Roi Et', 'Sa Kaeo', 'Sakon Nakhon', 'Samut Prakan', 'Samut Sakhon',
    'Samut Songkhram', 'Sara Buri', 'Satun', 'Sing Buri', 'Sisaket', 'Songkhla',
    'Sukhothai', 'Suphan Buri', 'Surat Thani', 'Surin', 'Tak', 'Trang', 'Trat',
    'Ubon Ratchathani', 'Udon Thani', 'Uthai Thani', 'Uttaradit', 'Yala', 'Yasothon'
  ]},
  { code: 'TR', name: 'Turkey', states: [
    'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Aksaray', 'Amasya', 'Ankara',
    'Antalya', 'Ardahan', 'Artvin', 'Aydın', 'Balıkesir', 'Bartın', 'Batman',
    'Bayburt', 'Bilecik', 'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa',
    'Çanakkale', 'Çankırı', 'Çorum', 'Denizli', 'Diyarbakır', 'Düzce', 'Edirne',
    'Elazığ', 'Erzincan', 'Erzurum', 'Eskişehir', 'Gaziantep', 'Giresun', 'Gümüşhane',
    'Hakkâri', 'Hatay', 'Iğdır', 'Isparta', 'İstanbul', 'İzmir', 'Kahramanmaraş',
    'Karabük', 'Karaman', 'Kars', 'Kastamonu', 'Kayseri', 'Kırıkkale', 'Kırklareli',
    'Kırşehir', 'Kilis', 'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa',
    'Mardin', 'Mersin', 'Muğla', 'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Osmaniye',
    'Rize', 'Sakarya', 'Samsun', 'Siirt', 'Sinop', 'Sivas', 'Şanlıurfa', 'Şırnak',
    'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Uşak', 'Van', 'Yalova', 'Yozgat', 'Zonguldak'
  ]},

  // U
  { code: 'UA', name: 'Ukraine', states: [
    'Cherkasy', 'Chernihiv', 'Chernivtsi', 'Crimea', 'Dnipropetrovsk', 'Donetsk',
    'Ivano-Frankivsk', 'Kharkiv', 'Kherson', 'Khmelnytskyi', 'Kiev', 'Kirovohrad',
    'Luhansk', 'Lviv', 'Mykolaiv', 'Odessa', 'Poltava', 'Rivne', 'Sumy',
    'Ternopil', 'Vinnytsia', 'Volyn', 'Zakarpattia', 'Zaporizhia', 'Zhytomyr'
  ]},
  { code: 'AE', name: 'United Arab Emirates', states: [
    'Abu Dhabi', 'Ajman', 'Dubai', 'Fujairah', 'Ras Al Khaimah', 'Sharjah', 'Umm Al Quwain'
  ]},
  { code: 'GB', name: 'United Kingdom', states: [
    'England', 'Scotland', 'Wales', 'Northern Ireland'
  ]},
  { code: 'US', name: 'United States', states: [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming', 'District of Columbia', 'Puerto Rico', 'US Virgin Islands',
    'American Samoa', 'Guam', 'Northern Mariana Islands'
  ]},

  // V-Z
  { code: 'VN', name: 'Vietnam', states: [
    'An Giang', 'Ba Ria-Vung Tau', 'Bac Giang', 'Bac Kan', 'Bac Lieu', 'Bac Ninh',
    'Ben Tre', 'Binh Dinh', 'Binh Duong', 'Binh Phuoc', 'Binh Thuan', 'Ca Mau',
    'Can Tho', 'Cao Bang', 'Da Nang', 'Dak Lak', 'Dak Nong', 'Dien Bien',
    'Dong Nai', 'Dong Thap', 'Gia Lai', 'Ha Giang', 'Ha Nam', 'Ha Noi',
    'Ha Tinh', 'Hai Duong', 'Hai Phong', 'Hau Giang', 'Ho Chi Minh', 'Hoa Binh',
    'Hung Yen', 'Khanh Hoa', 'Kien Giang', 'Kon Tum', 'Lai Chau', 'Lam Dong',
    'Lang Son', 'Lao Cai', 'Long An', 'Nam Dinh', 'Nghe An', 'Ninh Binh',
    'Ninh Thuan', 'Phu Tho', 'Phu Yen', 'Quang Binh', 'Quang Nam', 'Quang Ngai',
    'Quang Ninh', 'Quang Tri', 'Soc Trang', 'Son La', 'Tay Ninh', 'Thai Binh',
    'Thai Nguyen', 'Thanh Hoa', 'Thua Thien Hue', 'Tien Giang', 'Tra Vinh',
    'Tuyen Quang', 'Vinh Long', 'Vinh Phuc', 'Yen Bai'
  ]},
  { code: 'AR', name: 'Argentina', states: [
    'Buenos Aires', 'Catamarca', 'Chaco', 'Chubut', 'Córdoba', 'Corrientes', 'Entre Ríos',
    'Formosa', 'Jujuy', 'La Pampa', 'La Rioja', 'Mendoza', 'Misiones', 'Neuquén',
    'Río Negro', 'Salta', 'San Juan', 'San Luis', 'Santa Cruz', 'Santa Fe',
    'Santiago del Estero', 'Tierra del Fuego', 'Tucumán', 'Ciudad Autónoma de Buenos Aires'
  ]},
  { code: 'GB', name: 'United Kingdom', states: [
    'England', 'Scotland', 'Wales', 'Northern Ireland'
  ]},
  { code: 'FR', name: 'France', states: [
    'Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté', 'Brittany', 'Centre-Val de Loire',
    'Corsica', 'Grand Est', 'Hauts-de-France', 'Île-de-France', 'Normandy', 'Nouvelle-Aquitaine',
    'Occitanie', 'Pays de la Loire', 'Provence-Alpes-Côte d\'Azur'
  ]},
  { code: 'DE', name: 'Germany', states: [
    'Baden-Württemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg',
    'Hesse', 'Lower Saxony', 'Mecklenburg-Vorpommern', 'North Rhine-Westphalia',
    'Rhineland-Palatinate', 'Saarland', 'Saxony', 'Saxony-Anhalt', 'Schleswig-Holstein', 'Thuringia'
  ]},
  { code: 'IN', name: 'India', states: [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
    'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
    'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
    'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry'
  ]},
  { code: 'CN', name: 'China', states: [
    'Beijing', 'Shanghai', 'Tianjin', 'Chongqing', 'Guangdong', 'Jiangsu', 'Shandong', 'Zhejiang',
    'Henan', 'Sichuan', 'Hubei', 'Hunan', 'Anhui', 'Hebei', 'Jiangxi', 'Shanxi', 'Liaoning',
    'Fujian', 'Shaanxi', 'Heilongjiang', 'Guangxi', 'Yunnan', 'Jilin', 'Guizhou', 'Gansu',
    'Inner Mongolia', 'Xinjiang', 'Hainan', 'Ningxia', 'Qinghai', 'Tibet', 'Hong Kong', 'Macau'
  ]},
  { code: 'AU', name: 'Australia', states: [
    'Australian Capital Territory', 'New South Wales', 'Northern Territory', 'Queensland',
    'South Australia', 'Tasmania', 'Victoria', 'Western Australia'
  ]},
  { code: 'NZ', name: 'New Zealand', states: [
    'Auckland', 'Bay of Plenty', 'Canterbury', 'Gisborne', 'Hawke\'s Bay', 'Manawatu-Wanganui',
    'Marlborough', 'Nelson', 'Northland', 'Otago', 'Southland', 'Taranaki', 'Tasman',
    'Waikato', 'Wellington', 'West Coast'
  ]},
  { code: 'JP', name: 'Japan', states: [
    'Hokkaido', 'Aomori', 'Iwate', 'Miyagi', 'Akita', 'Yamagata', 'Fukushima', 'Ibaraki',
    'Tochigi', 'Gunma', 'Saitama', 'Chiba', 'Tokyo', 'Kanagawa', 'Niigata', 'Toyama',
    'Ishikawa', 'Fukui', 'Yamanashi', 'Nagano', 'Gifu', 'Shizuoka', 'Aichi', 'Mie',
    'Shiga', 'Kyoto', 'Osaka', 'Hyogo', 'Nara', 'Wakayama', 'Tottori', 'Shimane',
    'Okayama', 'Hiroshima', 'Yamaguchi', 'Tokushima', 'Kagawa', 'Ehime', 'Kochi',
    'Fukuoka', 'Saga', 'Nagasaki', 'Kumamoto', 'Oita', 'Miyazaki', 'Kagoshima', 'Okinawa'
  ]},
  { code: 'KR', name: 'South Korea', states: [
    'Seoul', 'Busan', 'Daegu', 'Incheon', 'Gwangju', 'Daejeon', 'Ulsan', 'Sejong',
    'Gyeonggi', 'Gangwon', 'North Chungcheong', 'South Chungcheong', 'North Jeolla',
    'South Jeolla', 'North Gyeongsang', 'South Gyeongsang', 'Jeju'
  ]},
  { code: 'SG', name: 'Singapore', states: [
    'Central Region', 'East Region', 'North Region', 'North-East Region', 'West Region'
  ]},
  { code: 'MY', name: 'Malaysia', states: [
    'Johor', 'Kedah', 'Kelantan', 'Malacca', 'Negeri Sembilan', 'Pahang', 'Penang',
    'Perak', 'Perlis', 'Sabah', 'Sarawak', 'Selangor', 'Terengganu', 'Kuala Lumpur',
    'Labuan', 'Putrajaya'
  ]},
  { code: 'TH', name: 'Thailand', states: [
    'Bangkok', 'Amnat Charoen', 'Ang Thong', 'Bueng Kan', 'Buriram', 'Chachoengsao',
    'Chai Nat', 'Chaiyaphum', 'Chanthaburi', 'Chiang Mai', 'Chiang Rai', 'Chonburi',
    'Chumphon', 'Kalasin', 'Kamphaeng Phet', 'Kanchanaburi', 'Khon Kaen', 'Krabi',
    'Lampang', 'Lamphun', 'Loei', 'Lopburi', 'Mae Hong Son', 'Maha Sarakham',
    'Mukdahan', 'Nakhon Nayok', 'Nakhon Pathom', 'Nakhon Phanom', 'Nakhon Ratchasima',
    'Nakhon Sawan', 'Nakhon Si Thammarat', 'Nan', 'Narathiwat', 'Nong Bua Lamphu',
    'Nong Khai', 'Nonthaburi', 'Pathum Thani', 'Pattani', 'Phang Nga', 'Phatthalung',
    'Phayao', 'Phetchabun', 'Phetchaburi', 'Phichit', 'Phitsanulok', 'Phrae',
    'Phuket', 'Prachinburi', 'Prachuap Khiri Khan', 'Ranong', 'Ratchaburi', 'Rayong',
    'Roi Et', 'Sa Kaeo', 'Sakon Nakhon', 'Samut Prakan', 'Samut Sakhon', 'Samut Songkhram',
    'Sara Buri', 'Satun', 'Sing Buri', 'Sisaket', 'Songkhla', 'Sukhothai', 'Suphan Buri',
    'Surat Thani', 'Surin', 'Tak', 'Trang', 'Trat', 'Ubon Ratchathani', 'Udon Thani',
    'Uthai Thani', 'Uttaradit', 'Yala', 'Yasothon'
  ]},
  { code: 'VN', name: 'Vietnam', states: [
    'An Giang', 'Bà Rịa–Vũng Tàu', 'Bắc Giang', 'Bắc Kạn', 'Bạc Liêu', 'Bắc Ninh',
    'Bến Tre', 'Bình Định', 'Bình Dương', 'Bình Phước', 'Bình Thuận', 'Cà Mau',
    'Cao Bằng', 'Đắk Lắk', 'Đắk Nông', 'Điện Biên', 'Đồng Nai', 'Đồng Tháp',
    'Gia Lai', 'Hà Giang', 'Hà Nam', 'Hà Tĩnh', 'Hải Dương', 'Hậu Giang',
    'Hòa Bình', 'Hưng Yên', 'Khánh Hòa', 'Kiên Giang', 'Kon Tum', 'Lai Châu',
    'Lâm Đồng', 'Lạng Sơn', 'Lào Cai', 'Long An', 'Nam Định', 'Nghệ An',
    'Ninh Bình', 'Ninh Thuận', 'Phú Thọ', 'Phú Yên', 'Quảng Bình', 'Quảng Nam',
    'Quảng Ngãi', 'Quảng Ninh', 'Quảng Trị', 'Sóc Trăng', 'Sơn La', 'Tây Ninh',
    'Thái Bình', 'Thái Nguyên', 'Thanh Hóa', 'Thừa Thiên Huế', 'Tiền Giang',
    'Trà Vinh', 'Tuyên Quang', 'Vĩnh Long', 'Vĩnh Phúc', 'Yên Bái', 'Hanoi',
    'Ho Chi Minh City', 'Da Nang', 'Hai Phong', 'Can Tho'
  ]},
  { code: 'PH', name: 'Philippines', states: [
    'Abra', 'Agusan del Norte', 'Agusan del Sur', 'Aklan', 'Albay', 'Antique',
    'Apayao', 'Aurora', 'Basilan', 'Bataan', 'Batanes', 'Batangas', 'Benguet',
    'Biliran', 'Bohol', 'Bukidnon', 'Bulacan', 'Cagayan', 'Camarines Norte',
    'Camarines Sur', 'Camiguin', 'Capiz', 'Catanduanes', 'Cavite', 'Cebu',
    'Compostela Valley', 'Cotabato', 'Davao del Norte', 'Davao del Sur',
    'Davao Oriental', 'Dinagat Islands', 'Eastern Samar', 'Guimaras', 'Ifugao',
    'Ilocos Norte', 'Ilocos Sur', 'Iloilo', 'Isabela', 'Kalinga', 'Laguna',
    'Lanao del Norte', 'Lanao del Sur', 'La Union', 'Leyte', 'Maguindanao',
    'Marinduque', 'Masbate', 'Mindoro Occidental', 'Mindoro Oriental',
    'Misamis Occidental', 'Misamis Oriental', 'Mountain Province', 'Negros Occidental',
    'Negros Oriental', 'Northern Samar', 'Nueva Ecija', 'Nueva Vizcaya', 'Palawan',
    'Pampanga', 'Pangasinan', 'Quezon', 'Quirino', 'Rizal', 'Romblon', 'Samar',
    'Sarangani', 'Siquijor', 'Sorsogon', 'South Cotabato', 'Southern Leyte',
    'Sultan Kudarat', 'Sulu', 'Surigao del Norte', 'Surigao del Sur', 'Tarlac',
    'Tawi-Tawi', 'Zambales', 'Zamboanga del Norte', 'Zamboanga del Sur',
    'Zamboanga Sibugay', 'Metro Manila'
  ]},
  { code: 'ID', name: 'Indonesia', states: [
    'Aceh', 'Bali', 'Banten', 'Bengkulu', 'Central Java', 'Central Kalimantan',
    'Central Sulawesi', 'East Java', 'East Kalimantan', 'East Nusa Tenggara',
    'Gorontalo', 'Jakarta', 'Jambi', 'Lampung', 'Maluku', 'North Kalimantan',
    'North Maluku', 'North Sulawesi', 'North Sumatra', 'Papua', 'Riau',
    'Riau Islands', 'South Kalimantan', 'South Sulawesi', 'South Sumatra',
    'Southeast Sulawesi', 'West Java', 'West Kalimantan', 'West Nusa Tenggara',
    'West Papua', 'West Sulawesi', 'West Sumatra', 'Yogyakarta'
  ]},
  { code: 'PK', name: 'Pakistan', states: [
    'Balochistan', 'Khyber Pakhtunkhwa', 'Punjab', 'Sindh', 'Gilgit-Baltistan',
    'Azad Jammu and Kashmir', 'Islamabad Capital Territory'
  ]},
  { code: 'BD', name: 'Bangladesh', states: [
    'Barisal', 'Chittagong', 'Dhaka', 'Khulna', 'Mymensingh', 'Rajshahi',
    'Rangpur', 'Sylhet'
  ]},
  { code: 'LK', name: 'Sri Lanka', states: [
    'Central', 'Eastern', 'North Central', 'Northern', 'North Western',
    'Sabaragamuwa', 'Southern', 'Uva', 'Western'
  ]},
  { code: 'MM', name: 'Myanmar', states: [
    'Ayeyarwady', 'Bago', 'Magway', 'Mandalay', 'Sagaing', 'Tanintharyi', 'Yangon',
    'Chin', 'Kachin', 'Kayah', 'Kayin', 'Mon', 'Rakhine', 'Shan', 'Naypyidaw'
  ]},
  { code: 'KH', name: 'Cambodia', states: [
    'Banteay Meanchey', 'Battambang', 'Kampong Cham', 'Kampong Chhnang',
    'Kampong Speu', 'Kampong Thom', 'Kampot', 'Kandal', 'Kep', 'Koh Kong',
    'Kratie', 'Mondulkiri', 'Oddar Meanchey', 'Pailin', 'Phnom Penh',
    'Preah Vihear', 'Prey Veng', 'Pursat', 'Ratanakiri', 'Siem Reap',
    'Sihanoukville', 'Stung Treng', 'Svay Rieng', 'Takeo', 'Tbong Khmum'
  ]},
  { code: 'LA', name: 'Laos', states: [
    'Attapeu', 'Bokeo', 'Bolikhamsai', 'Champasak', 'Houaphanh', 'Khammouane',
    'Luang Namtha', 'Luang Prabang', 'Oudomxay', 'Phongsaly', 'Salavan',
    'Savannakhet', 'Sekong', 'Vientiane', 'Vientiane Prefecture', 'Xaignabouli',
    'Xaisomboun', 'Xieng Khouang'
  ]},
  { code: 'MN', name: 'Mongolia', states: [
    'Arkhangai', 'Bayan-Ölgii', 'Bayankhongor', 'Bulgan', 'Darkhan-Uul',
    'Dornod', 'Dornogovi', 'Dundgovi', 'Govi-Altai', 'Govisümber',
    'Khentii', 'Khovd', 'Khövsgöl', 'Ömnögovi', 'Orkhon', 'Övörkhangai',
    'Selenge', 'Sükhbaatar', 'Töv', 'Uvs', 'Zavkhan', 'Ulaanbaatar'
  ]},
  { code: 'RU', name: 'Russia', states: [
    'Adygea', 'Altai Krai', 'Altai Republic', 'Amur Oblast', 'Arkhangelsk Oblast',
    'Astrakhan Oblast', 'Bashkortostan', 'Belgorod Oblast', 'Bryansk Oblast',
    'Buryatia', 'Chechen Republic', 'Chelyabinsk Oblast', 'Chukotka',
    'Chuvashia', 'Dagestan', 'Ingushetia', 'Irkutsk Oblast', 'Ivanovo Oblast',
    'Jewish Autonomous Oblast', 'Kabardino-Balkaria', 'Kaliningrad Oblast',
    'Kalmykia', 'Kaluga Oblast', 'Kamchatka Krai', 'Karachay-Cherkessia',
    'Karelia', 'Kemerovo Oblast', 'Khabarovsk Krai', 'Khakassia',
    'Khanty-Mansi', 'Kirov Oblast', 'Komi', 'Kostroma Oblast', 'Krasnodar Krai',
    'Krasnoyarsk Krai', 'Kurgan Oblast', 'Kursk Oblast', 'Leningrad Oblast',
    'Lipetsk Oblast', 'Magadan Oblast', 'Mari El', 'Mordovia', 'Moscow',
    'Moscow Oblast', 'Murmansk Oblast', 'Nenets', 'Nizhny Novgorod Oblast',
    'North Ossetia', 'Novgorod Oblast', 'Novosibirsk Oblast', 'Omsk Oblast',
    'Orenburg Oblast', 'Oryol Oblast', 'Penza Oblast', 'Perm Krai',
    'Primorsky Krai', 'Pskov Oblast', 'Rostov Oblast', 'Ryazan Oblast',
    'Saint Petersburg', 'Sakha Republic', 'Sakhalin Oblast', 'Samara Oblast',
    'Saratov Oblast', 'Smolensk Oblast', 'Stavropol Krai', 'Sverdlovsk Oblast',
    'Tambov Oblast', 'Tatarstan', 'Tomsk Oblast', 'Tula Oblast', 'Tuva',
    'Tver Oblast', 'Tyumen Oblast', 'Udmurtia', 'Ulyanovsk Oblast',
    'Vladimir Oblast', 'Volgograd Oblast', 'Vologda Oblast', 'Voronezh Oblast',
    'Yamalo-Nenets', 'Yaroslavl Oblast', 'Zabaykalsky Krai'
  ]},
  { code: 'KZ', name: 'Kazakhstan', states: [
    'Akmola', 'Aktobe', 'Almaty', 'Atyrau', 'East Kazakhstan', 'Karaganda',
    'Kostanay', 'Kyzylorda', 'Mangystau', 'North Kazakhstan', 'Pavlodar',
    'South Kazakhstan', 'West Kazakhstan', 'Zhambyl', 'Almaty City', 'Nur-Sultan'
  ]},
  { code: 'UZ', name: 'Uzbekistan', states: [
    'Andijan', 'Bukhara', 'Fergana', 'Jizzakh', 'Karakalpakstan', 'Kashkadarya',
    'Khorezm', 'Namangan', 'Navoiy', 'Samarkand', 'Sirdaryo', 'Surkhandarya',
    'Tashkent', 'Tashkent City'
  ]},
  { code: 'KG', name: 'Kyrgyzstan', states: [
    'Batken', 'Chuy', 'Issyk-Kul', 'Jalal-Abad', 'Naryn', 'Osh', 'Talas',
    'Bishkek', 'Osh City'
  ]},
  { code: 'TJ', name: 'Tajikistan', states: [
    'Dushanbe', 'Gorno-Badakhshan', 'Khatlon', 'Sughd'
  ]},
  { code: 'TM', name: 'Turkmenistan', states: [
    'Ahal', 'Balkan', 'Dashoguz', 'Lebap', 'Mary', 'Ashgabat'
  ]},
  { code: 'GE', name: 'Georgia', states: [
    'Adjara', 'Guria', 'Imereti', 'Kakheti', 'Kvemo Kartli', 'Mtskheta-Mtianeti',
    'Racha-Lechkhumi', 'Samegrelo-Zemo Svaneti', 'Samtskhe-Javakheti',
    'Shida Kartli', 'Tbilisi', 'Abkhazia', 'South Ossetia'
  ]},
  { code: 'AZ', name: 'Azerbaijan', states: [
    'Absheron', 'Agdam', 'Agdash', 'Agstafa', 'Agsu', 'Astara', 'Baku',
    'Balaken', 'Barda', 'Beylagan', 'Bilasuvar', 'Dashkasan', 'Fizuli',
    'Ganja', 'Gobustan', 'Goranboy', 'Goychay', 'Hajigabul', 'Imishli',
    'Ismailli', 'Jabrayil', 'Jalilabad', 'Kalbajar', 'Kurdamir', 'Lachin',
    'Lankaran', 'Lerik', 'Masalli', 'Mingachevir', 'Naftalan', 'Nakhchivan',
    'Neftchala', 'Oghuz', 'Qabala', 'Qakh', 'Qazakh', 'Quba', 'Qubadli',
    'Qusar', 'Saatli', 'Sabirabad', 'Salyan', 'Shamakhi', 'Shamkir',
    'Shirvan', 'Shusha', 'Siazan', 'Sumgayit', 'Tartar', 'Tovuz',
    'Ujar', 'Yardimli', 'Yevlakh', 'Zangilan', 'Zaqatala', 'Zardab'
  ]},
  { code: 'AM', name: 'Armenia', states: [
    'Aragatsotn', 'Ararat', 'Armavir', 'Gegharkunik', 'Kotayk', 'Lori',
    'Shirak', 'Syunik', 'Tavush', 'Vayots Dzor', 'Yerevan'
  ]},
  { code: 'IR', name: 'Iran', states: [
    'Alborz', 'Ardabil', 'Azerbaijan East', 'Azerbaijan West', 'Bushehr',
    'Chaharmahal and Bakhtiari', 'Fars', 'Gilan', 'Golestan', 'Hamadan',
    'Hormozgan', 'Ilam', 'Isfahan', 'Kerman', 'Kermanshah', 'Khuzestan',
    'Kohgiluyeh and Boyer-Ahmad', 'Kurdistan', 'Lorestan', 'Markazi',
    'Mazandaran', 'North Khorasan', 'Qazvin', 'Qom', 'Razavi Khorasan',
    'Semnan', 'Sistan and Baluchestan', 'South Khorasan', 'Tehran',
    'Yazd', 'Zanjan'
  ]},
  { code: 'AF', name: 'Afghanistan', states: [
    'Badakhshan', 'Badghis', 'Baghlan', 'Balkh', 'Bamyan', 'Daykundi',
    'Farah', 'Faryab', 'Ghazni', 'Ghor', 'Helmand', 'Herat', 'Jowzjan',
    'Kabul', 'Kandahar', 'Kapisa', 'Khost', 'Kunar', 'Kunduz', 'Laghman',
    'Logar', 'Nangarhar', 'Nimroz', 'Nuristan', 'Paktia', 'Paktika',
    'Panjshir', 'Parwan', 'Samangan', 'Sar-e Pol', 'Takhar', 'Urozgan',
    'Wardak', 'Zabul'
  ]},
  { code: 'NP', name: 'Nepal', states: [
    'Bagmati', 'Gandaki', 'Karnali', 'Lumbini', 'Province No. 1',
    'Province No. 2', 'Sudurpashchim'
  ]},
  { code: 'BT', name: 'Bhutan', states: [
    'Bumthang', 'Chhukha', 'Dagana', 'Gasa', 'Haa', 'Lhuntse',
    'Mongar', 'Paro', 'Pemagatshel', 'Punakha', 'Samdrup Jongkhar',
    'Samtse', 'Sarpang', 'Thimphu', 'Trashigang', 'Trashiyangtse',
    'Trongsa', 'Tsirang', 'Wangdue Phodrang', 'Zhemgang'
  ]},
  { code: 'MV', name: 'Maldives', states: [
    'Addu City', 'Alif Alif', 'Alif Dhaal', 'Baa', 'Dhaalu', 'Faafu',
    'Gaafu Alif', 'Gaafu Dhaalu', 'Gnaviyani', 'Haa Alif', 'Haa Dhaalu',
    'Kaafu', 'Laamu', 'Lhaviyani', 'Malé', 'Meemu', 'Noonu', 'Raa',
    'Seenu', 'Shaviyani', 'Thaa', 'Vaavu'
  ]},
  
  // Middle East
  { code: 'AE', name: 'United Arab Emirates', states: [
    'Abu Dhabi', 'Ajman', 'Dubai', 'Fujairah', 'Ras Al Khaimah', 'Sharjah', 'Umm Al Quwain'
  ]},
  { code: 'SA', name: 'Saudi Arabia', states: [
    'Al Bahah', 'Al Hudud ash Shamaliyah', 'Al Jawf', 'Al Madinah', 'Al Qasim',
    'Ar Riyad', 'Ash Sharqiyah', 'Asir', 'Hail', 'Jazan', 'Makkah', 'Najran', 'Tabuk'
  ]},
  { code: 'KW', name: 'Kuwait', states: [
    'Al Ahmadi', 'Al Farwaniyah', 'Al Jahra', 'Capital', 'Hawalli', 'Mubarak Al-Kabeer'
  ]},
  { code: 'QA', name: 'Qatar', states: [
    'Ad Dawhah', 'Al Khor', 'Al Rayyan', 'Al Wakrah', 'Ash Shamal', 'Az Zaayin', 'Umm Salal'
  ]},
  { code: 'BH', name: 'Bahrain', states: [
    'Capital', 'Central', 'Muharraq', 'Northern', 'Southern'
  ]},
  { code: 'OM', name: 'Oman', states: [
    'Ad Dakhiliyah', 'Al Batinah North', 'Al Batinah South', 'Al Buraymi', 'Al Wusta',
    'Ash Sharqiyah North', 'Ash Sharqiyah South', 'Dhofar', 'Muscat', 'Musandam', 'Az Zahirah'
  ]},
  { code: 'YE', name: 'Yemen', states: [
    'Abyan', 'Aden', 'Al Bayda', 'Al Hudaydah', 'Al Jawf', 'Al Mahrah', 'Al Mahwit',
    'Amran', 'Dhamar', 'Hadramaut', 'Hajjah', 'Ibb', 'Lahij', 'Marib', 'Raymah',
    'Saada', 'Sanaa', 'Shabwah', 'Socotra', 'Taizz'
  ]},
  { code: 'IQ', name: 'Iraq', states: [
    'Al Anbar', 'Al Muthanna', 'Al Qadisiyyah', 'An Najaf', 'Arbil', 'As Sulaymaniyyah',
    'Babil', 'Baghdad', 'Basra', 'Dhi Qar', 'Diyala', 'Dohuk', 'Karbala', 'Kirkuk',
    'Maysan', 'Ninawa', 'Salah ad Din', 'Wasit'
  ]},
  { code: 'JO', name: 'Jordan', states: [
    'Ajloun', 'Amman', 'Aqaba', 'At Tafilah', 'Az Zarqa', 'Balqa', 'Irbid', 'Jarash',
    'Karak', 'Ma\'an', 'Madaba', 'Mafraq'
  ]},
  { code: 'LB', name: 'Lebanon', states: [
    'Akkar', 'Baalbek-Hermel', 'Beirut', 'Beqaa', 'Mount Lebanon', 'Nabatieh', 'North', 'South'
  ]},
  { code: 'SY', name: 'Syria', states: [
    'Aleppo', 'Al-Hasakah', 'As-Suwayda', 'Damascus', 'Daraa', 'Deir ez-Zor', 'Hama',
    'Homs', 'Idlib', 'Latakia', 'Quneitra', 'Raqqa', 'Rif Dimashq', 'Tartus'
  ]},
  { code: 'IL', name: 'Israel', states: [
    'Central', 'Haifa', 'Jerusalem', 'Northern', 'Southern', 'Tel Aviv'
  ]},
  { code: 'PS', name: 'Palestine', states: [
    'Gaza Strip', 'West Bank'
  ]},
  { code: 'TR', name: 'Turkey', states: [
    'Adana', 'Adiyaman', 'Afyonkarahisar', 'Agri', 'Aksaray', 'Amasya', 'Ankara', 'Antalya',
    'Ardahan', 'Artvin', 'Aydin', 'Balikesir', 'Bartin', 'Batman', 'Bayburt', 'Bilecik',
    'Bingol', 'Bitlis', 'Bolu', 'Burdur', 'Bursa', 'Canakkale', 'Cankiri', 'Corum',
    'Denizli', 'Diyarbakir', 'Duzce', 'Edirne', 'Elazig', 'Erzincan', 'Erzurum', 'Eskisehir',
    'Gaziantep', 'Giresun', 'Gumushane', 'Hakkari', 'Hatay', 'Igdir', 'Isparta', 'Istanbul',
    'Izmir', 'Kahramanmaras', 'Karabuk', 'Karaman', 'Kars', 'Kastamonu', 'Kayseri', 'Kilis',
    'Kirikkale', 'Kirklareli', 'Kirsehir', 'Kocaeli', 'Konya', 'Kutahya', 'Malatya', 'Manisa',
    'Mardin', 'Mersin', 'Mugla', 'Mus', 'Nevsehir', 'Nigde', 'Ordu', 'Osmaniye', 'Rize',
    'Sakarya', 'Samsun', 'Sanliurfa', 'Siirt', 'Sinop', 'Sirnak', 'Sivas', 'Tekirdag',
    'Tokat', 'Trabzon', 'Tunceli', 'Usak', 'Van', 'Yalova', 'Yozgat', 'Zonguldak'
  ]},
  
  // Africa
  { code: 'NG', name: 'Nigeria', states: [
    'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
    'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'Gombe', 'Imo', 'Jigawa',
    'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger',
    'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe',
    'Zamfara', 'Federal Capital Territory'
  ]},
  { code: 'ZA', name: 'South Africa', states: [
    'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
    'Mpumalanga', 'Northern Cape', 'North West', 'Western Cape'
  ]},
  { code: 'EG', name: 'Egypt', states: [
    'Alexandria', 'Aswan', 'Asyut', 'Beheira', 'Beni Suef', 'Cairo', 'Dakahlia',
    'Damietta', 'Faiyum', 'Gharbia', 'Giza', 'Ismailia', 'Kafr el-Sheikh', 'Luxor',
    'Matruh', 'Minya', 'Monufia', 'New Valley', 'North Sinai', 'Port Said',
    'Qalyubia', 'Qena', 'Red Sea', 'Sharqia', 'Sohag', 'South Sinai', 'Suez'
  ]},
  { code: 'KE', name: 'Kenya', states: [
    'Baringo', 'Bomet', 'Bungoma', 'Busia', 'Elgeyo-Marakwet', 'Embu', 'Garissa',
    'Homa Bay', 'Isiolo', 'Kajiado', 'Kakamega', 'Kericho', 'Kiambu', 'Kilifi',
    'Kirinyaga', 'Kisii', 'Kisumu', 'Kitui', 'Kwale', 'Laikipia', 'Lamu', 'Machakos',
    'Makueni', 'Mandera', 'Marsabit', 'Meru', 'Migori', 'Mombasa', 'Murang\'a',
    'Nairobi', 'Nakuru', 'Nandi', 'Narok', 'Nyamira', 'Nyandarua', 'Nyeri',
    'Samburu', 'Siaya', 'Taita-Taveta', 'Tana River', 'Tharaka-Nithi', 'Trans Nzoia',
    'Turkana', 'Uasin Gishu', 'Vihiga', 'Wajir', 'West Pokot'
  ]},
  { code: 'UG', name: 'Uganda', states: [
    'Abim', 'Adjumani', 'Agago', 'Alebtong', 'Amolatar', 'Amudat', 'Amuria', 'Amuru',
    'Apac', 'Arua', 'Budaka', 'Bududa', 'Bugiri', 'Buhweju', 'Buikwe', 'Bukedea',
    'Bukomansimbi', 'Bukwo', 'Bulambuli', 'Buliisa', 'Bundibugyo', 'Bushenyi',
    'Busia', 'Butaleja', 'Butambala', 'Buvuma', 'Buyende', 'Dokolo', 'Gomba',
    'Gulu', 'Hoima', 'Ibanda', 'Iganga', 'Isingiro', 'Jinja', 'Kaabong', 'Kabale',
    'Kabarole', 'Kaberamaido', 'Kalangala', 'Kaliro', 'Kampala', 'Kamuli', 'Kamwenge',
    'Kanungu', 'Kapchorwa', 'Kasese', 'Katakwi', 'Kayunga', 'Kibaale', 'Kiboga',
    'Kibuku', 'Kiruhura', 'Kiryandongo', 'Kisoro', 'Kitgum', 'Koboko', 'Kole',
    'Kotido', 'Kumi', 'Kween', 'Kyankwanzi', 'Kyegegwa', 'Kyenjojo', 'Lamwo',
    'Lira', 'Luuka', 'Luwero', 'Lwengo', 'Lyantonde', 'Manafwa', 'Maracha',
    'Masaka', 'Masindi', 'Mayuge', 'Mbale', 'Mbarara', 'Mitooma', 'Mityana',
    'Mokono', 'Moroto', 'Moyo', 'Mpigi', 'Mubende', 'Mukono', 'Nakapiripirit',
    'Nakaseke', 'Nakasongola', 'Namayingo', 'Namutumba', 'Napak', 'Nebbi',
    'Ngora', 'Ntoroko', 'Ntungamo', 'Nwoya', 'Otuke', 'Oyam', 'Pader', 'Pallisa',
    'Rakai', 'Rubirizi', 'Rukungiri', 'Sembabule', 'Serere', 'Sheema', 'Sironko',
    'Soroti', 'Tororo', 'Wakiso', 'Yumbe', 'Zombo'
  ]},
  { code: 'TZ', name: 'Tanzania', states: [
    'Arusha', 'Dar es Salaam', 'Dodoma', 'Geita', 'Iringa', 'Kagera', 'Katavi',
    'Kigoma', 'Kilimanjaro', 'Lindi', 'Manyara', 'Mara', 'Mbeya', 'Morogoro',
    'Mtwara', 'Mwanza', 'Njombe', 'Pemba North', 'Pemba South', 'Pwani', 'Rukwa',
    'Ruvuma', 'Shinyanga', 'Simiyu', 'Singida', 'Tabora', 'Tanga', 'Unguja North',
    'Unguja South', 'Zanzibar Central/South', 'Zanzibar North', 'Zanzibar Urban/West'
  ]},
  { code: 'ET', name: 'Ethiopia', states: [
    'Addis Ababa', 'Afar', 'Amhara', 'Benishangul-Gumuz', 'Dire Dawa', 'Gambela',
    'Harari', 'Oromia', 'Sidama', 'Somali', 'Southern Nations', 'Tigray'
  ]},
  { code: 'GH', name: 'Ghana', states: [
    'Ahafo', 'Ashanti', 'Bono', 'Bono East', 'Central', 'Eastern', 'Greater Accra',
    'North East', 'Northern', 'Oti', 'Savannah', 'Upper East', 'Upper West',
    'Volta', 'Western', 'Western North'
  ]},
  { code: 'CI', name: 'Ivory Coast', states: [
    'Abidjan', 'Agnéby-Tiassa', 'Bafing', 'Bagoué', 'Béré', 'Bounkani', 'Cavally',
    'Folon', 'Gbêkê', 'Gbôklé', 'Gôh', 'Gontougo', 'Grands-Ponts', 'Guémon',
    'Hambol', 'Haut-Sassandra', 'Iffou', 'Indénié-Djuablin', 'Kabadougou',
    'Lôh-Djiboua', 'Marahoué', 'Moronou', 'Nawa', 'N\'zi', 'Poro', 'San-Pédro',
    'Tchologo', 'Tonkpi', 'Worodougou', 'Yamoussoukro'
  ]},
  { code: 'SN', name: 'Senegal', states: [
    'Dakar', 'Diourbel', 'Fatick', 'Kaffrine', 'Kaolack', 'Kédougou', 'Kolda',
    'Louga', 'Matam', 'Saint-Louis', 'Sédhiou', 'Tambacounda', 'Thiès', 'Ziguinchor'
  ]},
  { code: 'ML', name: 'Mali', states: [
    'Bamako', 'Gao', 'Kayes', 'Kidal', 'Koulikoro', 'Ménaka', 'Mopti', 'Ségou',
    'Sikasso', 'Taoudénit', 'Timbuktu'
  ]},
  { code: 'BF', name: 'Burkina Faso', states: [
    'Boucle du Mouhoun', 'Cascades', 'Centre', 'Centre-Est', 'Centre-Nord',
    'Centre-Ouest', 'Centre-Sud', 'Est', 'Hauts-Bassins', 'Nord', 'Plateau-Central',
    'Sahel', 'Sud-Ouest'
  ]},
  { code: 'NE', name: 'Niger', states: [
    'Agadez', 'Diffa', 'Dosso', 'Maradi', 'Niamey', 'Tahoua', 'Tillabéri', 'Zinder'
  ]},
  { code: 'TG', name: 'Togo', states: [
    'Centrale', 'Kara', 'Maritime', 'Plateaux', 'Savanes'
  ]},
  { code: 'BJ', name: 'Benin', states: [
    'Alibori', 'Atacora', 'Atlantique', 'Borgou', 'Collines', 'Couffo', 'Donga',
    'Littoral', 'Mono', 'Ouémé', 'Plateau', 'Zou'
  ]},
  { code: 'MU', name: 'Mauritius', states: [
    'Black River', 'Flacq', 'Grand Port', 'Moka', 'Pamplemousses', 'Plaines Wilhems',
    'Port Louis', 'Rivière du Rempart', 'Savanne', 'Rodrigues', 'Agaléga', 'Saint Brandon'
  ]},
  { code: 'LR', name: 'Liberia', states: [
    'Bomi', 'Bong', 'Gbarpolu', 'Grand Bassa', 'Grand Cape Mount', 'Grand Gedeh',
    'Grand Kru', 'Lofa', 'Margibi', 'Maryland', 'Montserrado', 'Nimba',
    'River Cess', 'River Gee', 'Sinoe'
  ]},
  { code: 'SL', name: 'Sierra Leone', states: [
    'Eastern', 'Northern', 'North West', 'Southern', 'Western Area'
  ]},
  { code: 'TD', name: 'Chad', states: [
    'Bahr el Gazel', 'Batha', 'Borkou', 'Chari-Baguirmi', 'Ennedi-Est', 'Ennedi-Ouest',
    'Guéra', 'Hadjer-Lamis', 'Kanem', 'Lac', 'Logone Occidental', 'Logone Oriental',
    'Mandoul', 'Mayo-Kebbi Est', 'Mayo-Kebbi Ouest', 'Moyen-Chari', 'N\'Djamena',
    'Ouaddaï', 'Salamat', 'Sila', 'Tandjilé', 'Tibesti', 'Wadi Fira'
  ]},
  { code: 'CF', name: 'Central African Republic', states: [
    'Bamingui-Bangoran', 'Bangui', 'Basse-Kotto', 'Haute-Kotto', 'Haut-Mbomou',
    'Kémo', 'Lobaye', 'Mambéré-Kadéï', 'Mbomou', 'Nana-Grébizi', 'Nana-Mambéré',
    'Ombella-M\'Poko', 'Ouaka', 'Ouham', 'Ouham-Pendé', 'Sangha-Mbaéré', 'Vakaga'
  ]},
  { code: 'CM', name: 'Cameroon', states: [
    'Adamawa', 'Centre', 'East', 'Far North', 'Littoral', 'North', 'Northwest',
    'South', 'Southwest', 'West'
  ]},
  { code: 'CV', name: 'Cape Verde', states: [
    'Boa Vista', 'Brava', 'Fogo', 'Maio', 'Sal', 'Santiago', 'Santo Antão',
    'São Nicolau', 'São Vicente', 'Tarrafal'
  ]},
  { code: 'ST', name: 'São Tomé and Príncipe', states: [
    'Príncipe', 'São Tomé'
  ]},
  { code: 'GQ', name: 'Equatorial Guinea', states: [
    'Annobón', 'Bioko Norte', 'Bioko Sur', 'Centro Sur', 'Kié-Ntem', 'Litoral', 'Wele-Nzas'
  ]},
  { code: 'GA', name: 'Gabon', states: [
    'Estuaire', 'Haut-Ogooué', 'Moyen-Ogooué', 'Ngounié', 'Nyanga', 'Ogooué-Ivindo',
    'Ogooué-Lolo', 'Ogooué-Maritime', 'Woleu-Ntem'
  ]},
  { code: 'CG', name: 'Republic of the Congo', states: [
    'Bouenza', 'Brazzaville', 'Cuvette', 'Cuvette-Ouest', 'Kouilou', 'Lékoumou',
    'Likouala', 'Niari', 'Plateaux', 'Pointe-Noire', 'Pool', 'Sangha'
  ]},
  { code: 'CD', name: 'Democratic Republic of the Congo', states: [
    'Bas-Uele', 'Équateur', 'Haut-Katanga', 'Haut-Lomami', 'Haut-Uele', 'Ituri',
    'Kasaï', 'Kasaï-Central', 'Kasaï-Oriental', 'Kinshasa', 'Kongo Central',
    'Kwango', 'Kwilu', 'Lomami', 'Lualaba', 'Mai-Ndombe', 'Maniema', 'Mongala',
    'Nord-Kivu', 'Nord-Ubangi', 'Sankuru', 'Sud-Kivu', 'Sud-Ubangi', 'Tanganyika',
    'Tshopo', 'Tshuapa'
  ]},
  { code: 'AO', name: 'Angola', states: [
    'Bengo', 'Benguela', 'Bié', 'Cabinda', 'Cuando Cubango', 'Cuanza Norte',
    'Cuanza Sul', 'Cunene', 'Huambo', 'Huíla', 'Luanda', 'Lunda Norte',
    'Lunda Sul', 'Malanje', 'Moxico', 'Namibe', 'Uíge', 'Zaire'
  ]},
  { code: 'GW', name: 'Guinea-Bissau', states: [
    'Bafatá', 'Biombo', 'Bissau', 'Bolama', 'Cacheu', 'Gabú', 'Oio', 'Quinara', 'Tombali'
  ]},
  { code: 'SC', name: 'Seychelles', states: [
    'Anse aux Pins', 'Anse Boileau', 'Anse Etoile', 'Anse Louis', 'Anse Royale',
    'Baie Lazare', 'Baie Sainte Anne', 'Beau Vallon', 'Bel Air', 'Bel Ombre',
    'Cascade', 'Glacis', 'Grand Anse Mahe', 'Grand Anse Praslin', 'La Digue',
    'La Riviere Anglaise', 'Mont Buxton', 'Mont Fleuri', 'Plaisance', 'Pointe La Rue',
    'Port Glaud', 'Saint Louis', 'Takamaka'
  ]},
  { code: 'SD', name: 'Sudan', states: [
    'Al Jazirah', 'Al Qadarif', 'Blue Nile', 'Central Darfur', 'East Darfur',
    'Kassala', 'Khartoum', 'North Darfur', 'North Kordofan', 'Northern',
    'Red Sea', 'River Nile', 'Sennar', 'South Darfur', 'South Kordofan',
    'West Darfur', 'West Kordofan', 'White Nile'
  ]},
  { code: 'RW', name: 'Rwanda', states: [
    'Eastern', 'Kigali', 'Northern', 'Southern', 'Western'
  ]},
  { code: 'SO', name: 'Somalia', states: [
    'Awdal', 'Bakool', 'Banaadir', 'Bari', 'Bay', 'Galguduud', 'Gedo',
    'Hiiraan', 'Jubbada Dhexe', 'Jubbada Hoose', 'Mudug', 'Nugaal',
    'Sanaag', 'Shabeellaha Dhexe', 'Shabeellaha Hoose', 'Sool', 'Togdheer', 'Woqooyi Galbeed'
  ]},
  { code: 'DJ', name: 'Djibouti', states: [
    'Ali Sabieh', 'Arta', 'Dikhil', 'Djibouti', 'Obock', 'Tadjourah'
  ]},
  { code: 'BI', name: 'Burundi', states: [
    'Bubanza', 'Bujumbura Mairie', 'Bujumbura Rural', 'Bururi', 'Cankuzo',
    'Cibitoke', 'Gitega', 'Karuzi', 'Kayanza', 'Kirundo', 'Makamba',
    'Muramvya', 'Muyinga', 'Mwaro', 'Ngozi', 'Rumonge', 'Rutana', 'Ruyigi'
  ]},
  { code: 'MZ', name: 'Mozambique', states: [
    'Cabo Delgado', 'Gaza', 'Inhambane', 'Manica', 'Maputo', 'Maputo City',
    'Nampula', 'Niassa', 'Sofala', 'Tete', 'Zambézia'
  ]},
  { code: 'ZM', name: 'Zambia', states: [
    'Central', 'Copperbelt', 'Eastern', 'Luapula', 'Lusaka', 'Muchinga',
    'Northern', 'North-Western', 'Southern', 'Western'
  ]},
  { code: 'MG', name: 'Madagascar', states: [
    'Antananarivo', 'Antsiranana', 'Fianarantsoa', 'Mahajanga', 'Toamasina', 'Toliara'
  ]},
  { code: 'ZW', name: 'Zimbabwe', states: [
    'Bulawayo', 'Harare', 'Manicaland', 'Mashonaland Central', 'Mashonaland East',
    'Mashonaland West', 'Masvingo', 'Matabeleland North', 'Matabeleland South', 'Midlands'
  ]},
  { code: 'NA', name: 'Namibia', states: [
    'Erongo', 'Hardap', 'Karas', 'Kavango East', 'Kavango West', 'Khomas',
    'Kunene', 'Ohangwena', 'Omaheke', 'Omusati', 'Oshana', 'Oshikoto',
    'Otjozondjupa', 'Zambezi'
  ]},
  { code: 'MW', name: 'Malawi', states: [
    'Balaka', 'Blantyre', 'Chikwawa', 'Chiradzulu', 'Chitipa', 'Dedza',
    'Dowa', 'Karonga', 'Kasungu', 'Likoma', 'Lilongwe', 'Machinga',
    'Mangochi', 'Mchinji', 'Mulanje', 'Mwanza', 'Mzimba', 'Neno',
    'Nkhata Bay', 'Nkhotakota', 'Nsanje', 'Ntcheu', 'Ntchisi', 'Phalombe',
    'Rumphi', 'Salima', 'Thyolo', 'Zomba'
  ]},
  { code: 'LS', name: 'Lesotho', states: [
    'Berea', 'Butha-Buthe', 'Leribe', 'Mafeteng', 'Maseru', 'Mohale\'s Hoek',
    'Mokhotlong', 'Qacha\'s Nek', 'Quthing', 'Thaba-Tseka'
  ]},
  { code: 'BW', name: 'Botswana', states: [
    'Central', 'Ghanzi', 'Kgalagadi', 'Kgatleng', 'Kweneng', 'North East',
    'North West', 'South East', 'Southern'
  ]},
  { code: 'SZ', name: 'Eswatini', states: [
    'Hhohho', 'Lubombo', 'Manzini', 'Shiselweni'
  ]},
  { code: 'KM', name: 'Comoros', states: [
    'Anjouan', 'Grande Comore', 'Mohéli'
  ]},
  { code: 'ER', name: 'Eritrea', states: [
    'Anseba', 'Debub', 'Debubawi Keyih Bahri', 'Gash-Barka', 'Maakel', 'Semenawi Keyih Bahri'
  ]},
  { code: 'MA', name: 'Morocco', states: [
    'Béni Mellal-Khénifra', 'Casablanca-Settat', 'Dakhla-Oued Ed-Dahab',
    'Drâa-Tafilalet', 'Fès-Meknès', 'Guelmim-Oued Noun', 'Laâyoune-Sakia El Hamra',
    'Marrakech-Safi', 'Oriental', 'Rabat-Salé-Kénitra', 'Souss-Massa', 'Tanger-Tétouan-Al Hoceïma'
  ]},
  { code: 'DZ', name: 'Algeria', states: [
    'Adrar', 'Aïn Defla', 'Aïn Témouchent', 'Alger', 'Annaba', 'Batna', 'Béchar',
    'Béjaïa', 'Biskra', 'Blida', 'Bordj Bou Arréridj', 'Bouira', 'Boumerdès',
    'Chlef', 'Constantine', 'Djelfa', 'El Bayadh', 'El Oued', 'El Tarf',
    'Ghardaïa', 'Guelma', 'Illizi', 'Jijel', 'Khenchela', 'Laghouat',
    'Mascara', 'Médéa', 'Mila', 'Mostaganem', 'M\'Sila', 'Naama', 'Oran',
    'Ouargla', 'Oum el Bouaghi', 'Relizane', 'Saïda', 'Sétif', 'Sidi Bel Abbès',
    'Skikda', 'Souk Ahras', 'Tamanrasset', 'Tébessa', 'Tiaret', 'Tindouf',
    'Tipaza', 'Tissemsilt', 'Tizi Ouzou', 'Tlemcen'
  ]},
  { code: 'TN', name: 'Tunisia', states: [
    'Ariana', 'Béja', 'Ben Arous', 'Bizerte', 'Gabès', 'Gafsa', 'Jendouba',
    'Kairouan', 'Kasserine', 'Kébili', 'Kef', 'Mahdia', 'Manouba', 'Médenine',
    'Monastir', 'Nabeul', 'Sfax', 'Sidi Bouzid', 'Siliana', 'Sousse',
    'Tataouine', 'Tozeur', 'Tunis', 'Zaghouan'
  ]},
  { code: 'LY', name: 'Libya', states: [
    'Al Butnan', 'Al Jabal al Akhdar', 'Al Jabal al Gharbi', 'Al Jifarah',
    'Al Kufrah', 'Al Marj', 'Al Marqab', 'Al Wahat', 'An Nuqat al Khams',
    'Az Zawiyah', 'Benghazi', 'Darnah', 'Ghat', 'Misratah', 'Murzuq',
    'Nalut', 'Sabha', 'Surt', 'Tarabulus', 'Wadi al Hayat', 'Wadi ash Shati'
  ]},
  
  // More European Countries
  { code: 'RO', name: 'Romania', states: [
    'Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani',
    'Brăila', 'Brașov', 'Bucharest', 'Buzău', 'Călărași', 'Caraș-Severin',
    'Cluj', 'Constanța', 'Covasna', 'Dâmbovița', 'Dolj', 'Galați', 'Giurgiu',
    'Gorj', 'Harghita', 'Hunedoara', 'Ialomița', 'Iași', 'Ilfov', 'Maramureș',
    'Mehedinți', 'Mureș', 'Neamț', 'Olt', 'Prahova', 'Sălaj', 'Satu Mare',
    'Sibiu', 'Suceava', 'Teleorman', 'Timiș', 'Tulcea', 'Vâlcea', 'Vaslui', 'Vrancea'
  ]},
  { code: 'BG', name: 'Bulgaria', states: [
    'Blagoevgrad', 'Burgas', 'Dobrich', 'Gabrovo', 'Haskovo', 'Kardzhali',
    'Kyustendil', 'Lovech', 'Montana', 'Pazardzhik', 'Pernik', 'Pleven',
    'Plovdiv', 'Razgrad', 'Ruse', 'Shumen', 'Silistra', 'Sliven', 'Smolyan',
    'Sofia', 'Sofia City', 'Stara Zagora', 'Targovishte', 'Varna', 'Veliko Tarnovo',
    'Vidin', 'Vratsa', 'Yambol'
  ]},
  
  // More European Countries
  { code: 'AL', name: 'Albania', states: [
    'Berat', 'Dibër', 'Durrës', 'Elbasan', 'Fier', 'Gjirokastër', 'Korçë',
    'Kukës', 'Lezhë', 'Shkodër', 'Tirana', 'Vlorë'
  ]},
  { code: 'MT', name: 'Malta', states: [
    'Central Region', 'Gozo Region', 'Northern Region', 'South Eastern Region', 'Southern Region'
  ]},
  { code: 'CY', name: 'Cyprus', states: [
    'Famagusta', 'Kyrenia', 'Larnaca', 'Limassol', 'Nicosia', 'Paphos'
  ]},
  { code: 'LT', name: 'Lithuania', states: [
    'Alytus', 'Kaunas', 'Klaipėda', 'Marijampolė', 'Panevėžys', 'Šiauliai',
    'Tauragė', 'Telšiai', 'Utena', 'Vilnius'
  ]},
  { code: 'LV', name: 'Latvia', states: [
    'Aizkraukle', 'Aluksne', 'Balvi', 'Bauska', 'Cēsis', 'Daugavpils', 'Dobele',
    'Gulbene', 'Jēkabpils', 'Jelgava', 'Krāslava', 'Kuldīga', 'Liepāja',
    'Limbaži', 'Ludza', 'Madona', 'Ogre', 'Preiļi', 'Rēzekne', 'Riga', 'Saldus',
    'Talsi', 'Tukums', 'Valka', 'Valmiera', 'Ventspils'
  ]},
  { code: 'EE', name: 'Estonia', states: [
    'Harju', 'Hiiu', 'Ida-Viru', 'Jõgeva', 'Järva', 'Lääne', 'Lääne-Viru',
    'Põlva', 'Pärnu', 'Rapla', 'Saare', 'Tartu', 'Valga', 'Viljandi', 'Võru'
  ]},
  { code: 'MD', name: 'Moldova', states: [
    'Anenii Noi', 'Basarabeasca', 'Briceni', 'Cahul', 'Cantemir', 'Călărași',
    'Căușeni', 'Cimișlia', 'Criuleni', 'Dondușeni', 'Drochia', 'Dubăsari',
    'Edineț', 'Fălești', 'Florești', 'Glodeni', 'Hîncești', 'Ialoveni',
    'Leova', 'Nisporeni', 'Ocnița', 'Orhei', 'Rezina', 'Rîșcani', 'Sîngerei',
    'Soroca', 'Strășeni', 'Șoldănești', 'Ștefan Vodă', 'Taraclia', 'Telenești',
    'Ungheni', 'Chișinău', 'Bălți', 'Bender', 'Comrat', 'Tiraspol'
  ]},
  { code: 'BY', name: 'Belarus', states: [
    'Brest', 'Gomel', 'Grodno', 'Minsk', 'Mogilev', 'Vitebsk', 'Minsk City'
  ]},
  { code: 'AD', name: 'Andorra', states: [
    'Andorra la Vella', 'Canillo', 'Encamp', 'Escaldes-Engordany', 'La Massana',
    'Ordino', 'Sant Julià de Lòria'
  ]},
  { code: 'MC', name: 'Monaco', states: [
    'Fontvieille', 'La Condamine', 'Monaco-Ville', 'Monte Carlo'
  ]},
  { code: 'SM', name: 'San Marino', states: [
    'Acquaviva', 'Borgo Maggiore', 'Chiesanuova', 'Domagnano', 'Faetano',
    'Fiorentino', 'Montegiardino', 'San Marino', 'Serravalle'
  ]},
  { code: 'VA', name: 'Vatican City', states: [
    'Vatican City'
  ]},
  { code: 'LI', name: 'Liechtenstein', states: [
    'Balzers', 'Eschen', 'Gamprin', 'Mauren', 'Planken', 'Ruggell',
    'Schaan', 'Schellenberg', 'Triesen', 'Triesenberg', 'Vaduz'
  ]},
  { code: 'UA', name: 'Ukraine', states: [
    'Cherkasy', 'Chernihiv', 'Chernivtsi', 'Crimea', 'Dnipropetrovsk', 'Donetsk',
    'Ivano-Frankivsk', 'Kharkiv', 'Kherson', 'Khmelnytskyi', 'Kiev', 'Kirovohrad',
    'Luhansk', 'Lviv', 'Mykolaiv', 'Odessa', 'Poltava', 'Rivne', 'Sumy',
    'Ternopil', 'Vinnytsia', 'Volyn', 'Zakarpattia', 'Zaporizhia', 'Zhytomyr'
  ]},
  { code: 'RS', name: 'Serbia', states: [
    'Belgrade', 'Bor', 'Braničevo', 'Jablanica', 'Kolubara', 'Mačva', 'Moravica',
    'Nišava', 'Pčinja', 'Pirot', 'Podunavlje', 'Pomoravlje', 'Rasina', 'Raška',
    'South Bačka', 'South Banat', 'Srem', 'Šumadija', 'Toplica', 'West Bačka',
    'West Banat', 'Zlatibor', 'Kosovo', 'Vojvodina'
  ]},
  { code: 'ME', name: 'Montenegro', states: [
    'Andrijevica', 'Bar', 'Berane', 'Bijelo Polje', 'Budva', 'Cetinje',
    'Danilovgrad', 'Gusinje', 'Herceg Novi', 'Kolašin', 'Kotor', 'Mojkovac',
    'Nikšić', 'Petnjica', 'Plav', 'Pljevlja', 'Plužine', 'Podgorica',
    'Rožaje', 'Šavnik', 'Tivat', 'Tuzi', 'Ulcinj', 'Žabljak'
  ]},
  { code: 'XK', name: 'Kosovo', states: [
    'Pristina', 'Mitrovica', 'Peja', 'Prizren', 'Ferizaj', 'Gjilan', 'Gjakova'
  ]},
  { code: 'HR', name: 'Croatia', states: [
    'Bjelovar-Bilogora', 'Brodsko-posavska', 'Dubrovnik-Neretva', 'Grad Zagreb',
    'Istria', 'Karlovac', 'Koprivnica-Križevci', 'Krapina-Zagorje', 'Lika-Senj',
    'Međimurje', 'Osijek-Baranja', 'Požega-Slavonia', 'Primorje-Gorski Kotar',
    'Šibenik-Knin', 'Sisak-Moslavina', 'Split-Dalmatia', 'Varaždin',
    'Virovitica-Podravina', 'Vukovar-Srijem', 'Zadar', 'Zagreb'
  ]},
  { code: 'SI', name: 'Slovenia', states: [
    'Ajdovščina', 'Ankaran', 'Apače', 'Beltinci', 'Benedikt', 'Bistrica ob Sotli',
    'Bled', 'Bloke', 'Bohinj', 'Borovnica', 'Bovec', 'Braslovče', 'Brda',
    'Brezovica', 'Brežice', 'Cankova', 'Celje', 'Cerklje na Gorenjskem',
    'Cerknica', 'Cerkno', 'Cerkvenjak', 'Cirkulane', 'Destrnik', 'Divača',
    'Dobje', 'Dobrepolje', 'Dobrna', 'Dobrova-Polhov Gradec', 'Dobrovnik',
    'Dol pri Ljubljani', 'Dolenjske Toplice', 'Domžale', 'Dornava', 'Dravograd',
    'Duplek', 'Gorenja Vas-Poljane', 'Gorišnica', 'Gornja Radgona', 'Gornji Grad',
    'Gornji Petrovci', 'Grad', 'Grosuplje', 'Hajdina', 'Hoče-Slivnica',
    'Hodoš', 'Horjul', 'Hrastnik', 'Hrpelje-Kozina', 'Idrija', 'Ig',
    'Ilirska Bistrica', 'Ivančna Gorica', 'Izola', 'Jesenice', 'Jezersko',
    'Juršinci', 'Kamnik', 'Kanal', 'Kidričevo', 'Kobarid', 'Kobilje',
    'Kočevje', 'Komen', 'Komenda', 'Koper', 'Kostanjevica na Krki', 'Kostel',
    'Kozje', 'Kranj', 'Kranjska Gora', 'Križevci', 'Krško', 'Kungota',
    'Kuzma', 'Laško', 'Lenart', 'Lendava', 'Litija', 'Ljubljana', 'Ljubno',
    'Ljutomer', 'Log-Dragomer', 'Logatec', 'Loška Dolina', 'Loški Potok',
    'Lovrenc na Pohorju', 'Luče', 'Lukovica', 'Majšperk', 'Makole',
    'Maribor', 'Markovci', 'Medvode', 'Mengeš', 'Metlika', 'Mežica',
    'Miklavž na Dravskem Polju', 'Miren-Kostanjevica', 'Mirna', 'Mirna Peč',
    'Mislinja', 'Mokronog-Trebelno', 'Moravče', 'Moravske Toplice', 'Mozirje',
    'Murska Sobota', 'Muta', 'Naklo', 'Nazarje', 'Nova Gorica', 'Novo Mesto',
    'Odranci', 'Oplotnica', 'Ormož', 'Osilnica', 'Pesnica', 'Piran',
    'Pivka', 'Podčetrtek', 'Podlehnik', 'Podvelka', 'Poljčane', 'Polzela',
    'Postojna', 'Prebold', 'Preddvor', 'Prevalje', 'Ptuj', 'Puconci',
    'Rače-Fram', 'Radeče', 'Radenci', 'Radlje ob Dravi', 'Radovljica',
    'Ravne na Koroškem', 'Razkrižje', 'Rečica ob Savinji', 'Renče-Vogrsko',
    'Ribnica', 'Ribnica na Pohorju', 'Rogaška Slatina', 'Rogašovci',
    'Rogatec', 'Ruše', 'Šalovci', 'Selnica ob Dravi', 'Semič', 'Sevnica',
    'Sežana', 'Slovenj Gradec', 'Slovenska Bistrica', 'Slovenske Konjice',
    'Šmarje pri Jelšah', 'Šmarješke Toplice', 'Šmartno ob Paki', 'Šmartno pri Litiji',
    'Sodražica', 'Solčava', 'Šoštanj', 'Starše', 'Štore', 'Straža',
    'Sveta Ana', 'Sveta Trojica v Slovenskih Goricah', 'Sveti Andraž v Slovenskih Goricah',
    'Sveti Jurij ob Ščavnici', 'Sveti Jurij v Slovenskih Goricah', 'Sveti Tomaž',
    'Šentilj', 'Šentjernej', 'Šentjur', 'Šentrupert', 'Škocjan', 'Škofja Loka',
    'Škofljica', 'Tabor', 'Tišina', 'Tolmin', 'Trbovlje', 'Trebnje',
    'Trnovska Vas', 'Trzin', 'Tržič', 'Turnišče', 'Velenje', 'Velika Polana',
    'Velike Lašče', 'Veržej', 'Videm', 'Vipava', 'Vitanje', 'Vodice',
    'Vojnik', 'Vransko', 'Vrhnika', 'Vuzenica', 'Zagorje ob Savi', 'Zavrč',
    'Zreče', 'Žalec', 'Železniki', 'Žetale', 'Žiri', 'Žirovnica',
    'Žužemberk'
  ]},
  { code: 'BA', name: 'Bosnia and Herzegovina', states: [
    'Brčko District', 'Federation of Bosnia and Herzegovina', 'Republika Srpska'
  ]},
  { code: 'MK', name: 'North Macedonia', states: [
    'Skopje', 'Pelagonia', 'Polog', 'Northeastern', 'Eastern', 'Southeastern',
    'Vardar', 'Southwest'
  ]},
  
  // Caribbean Countries
  { code: 'CU', name: 'Cuba', states: [
    'Artemisa', 'Camagüey', 'Ciego de Ávila', 'Cienfuegos', 'Granma', 'Guantánamo',
    'Havana', 'Holguín', 'Isle of Youth', 'Las Tunas', 'Matanzas', 'Mayabeque',
    'Pinar del Río', 'Sancti Spíritus', 'Santiago de Cuba', 'Villa Clara'
  ]},
  { code: 'BS', name: 'Bahamas', states: [
    'Acklins', 'Berry Islands', 'Bimini', 'Black Point', 'Cat Island', 'Central Abaco',
    'Central Andros', 'Central Eleuthera', 'City of Freeport', 'Crooked Island and Long Cay',
    'East Grand Bahama', 'Exuma', 'Grand Cay', 'Harbour Island', 'Hope Town',
    'Inagua', 'Long Island', 'Mangrove Cay', 'Mayaguana', 'Moore\'s Island',
    'Nassau', 'New Providence', 'North Abaco', 'North Andros', 'North Eleuthera',
    'Ragged Island', 'Rum Cay', 'San Salvador', 'South Abaco', 'South Andros',
    'South Eleuthera', 'Spanish Wells', 'West Grand Bahama'
  ]},
  { code: 'BB', name: 'Barbados', states: [
    'Christ Church', 'Saint Andrew', 'Saint George', 'Saint James', 'Saint John',
    'Saint Joseph', 'Saint Lucy', 'Saint Michael', 'Saint Peter', 'Saint Philip', 'Saint Thomas'
  ]},
  { code: 'TT', name: 'Trinidad and Tobago', states: [
    'Arima', 'Chaguanas', 'Couva-Tabaquite-Talparo', 'Diego Martin', 'Eastern Tobago',
    'Penal-Debe', 'Point Fortin', 'Port of Spain', 'Princes Town', 'Rio Claro-Mayaro',
    'San Fernando', 'San Juan-Laventille', 'Sangre Grande', 'Siparia', 'Tunapuna-Piarco',
    'Western Tobago'
  ]},
  { code: 'AG', name: 'Antigua and Barbuda', states: [
    'Barbuda', 'Redonda', 'Saint George', 'Saint John', 'Saint Mary', 'Saint Paul', 'Saint Peter', 'Saint Philip'
  ]},
  { code: 'DM', name: 'Dominica', states: [
    'Saint Andrew', 'Saint David', 'Saint George', 'Saint John', 'Saint Joseph',
    'Saint Luke', 'Saint Mark', 'Saint Patrick', 'Saint Paul', 'Saint Peter'
  ]},
  { code: 'GD', name: 'Grenada', states: [
    'Carriacou and Petite Martinique', 'Saint Andrew', 'Saint David', 'Saint George',
    'Saint John', 'Saint Mark', 'Saint Patrick'
  ]},
  { code: 'KN', name: 'Saint Kitts and Nevis', states: [
    'Christ Church Nichola Town', 'Saint Anne Sandy Point', 'Saint George Basseterre',
    'Saint George Gingerland', 'Saint James Windward', 'Saint John Capesterre',
    'Saint John Figtree', 'Saint Mary Cayon', 'Saint Paul Capesterre',
    'Saint Paul Charlestown', 'Saint Peter Basseterre', 'Saint Thomas Lowland',
    'Saint Thomas Middle Island', 'Trinity Palmetto Point'
  ]},
  { code: 'LC', name: 'Saint Lucia', states: [
    'Anse la Raye', 'Canaries', 'Castries', 'Choiseul', 'Dennery', 'Gros Islet',
    'Laborie', 'Micoud', 'Praslin', 'Soufrière', 'Vieux Fort'
  ]},
  { code: 'VC', name: 'Saint Vincent and the Grenadines', states: [
    'Charlotte', 'Grenadines', 'Saint Andrew', 'Saint David', 'Saint George', 'Saint Patrick'
  ]},
  
  // Pacific Countries
  { code: 'FJ', name: 'Fiji', states: [
    'Ba', 'Bua', 'Cakaudrove', 'Kadavu', 'Lau', 'Lomaiviti', 'Macuata', 'Nadroga-Navosa',
    'Naitasiri', 'Namosi', 'Ra', 'Rewa', 'Serua', 'Tailevu'
  ]},
  { code: 'WS', name: 'Samoa', states: [
    'A\'ana', 'Aiga-i-le-Tai', 'Atua', 'Fa\'asaleleaga', 'Gaga\'emauga', 'Gaga\'ifomauga',
    'Palauli', 'Satupa\'itea', 'Tuamasaga', 'Va\'a-o-Fonoti', 'Vaisigano'
  ]},
  { code: 'KI', name: 'Kiribati', states: [
    'Banaba', 'Central Gilberts', 'Line Islands', 'Northern Gilberts', 'Phoenix Islands', 'Southern Gilberts'
  ]},
  { code: 'TV', name: 'Tuvalu', states: [
    'Funafuti', 'Nanumanga', 'Nanumea', 'Niutao', 'Nui', 'Nukufetau', 'Nukulaelae', 'Vaitupu'
  ]},
  { code: 'FM', name: 'Micronesia', states: [
    'Chuuk', 'Kosrae', 'Pohnpei', 'Yap'
  ]},
  { code: 'MH', name: 'Marshall Islands', states: [
    'Ailinglaplap', 'Ailuk', 'Arno', 'Aur', 'Bikini', 'Ebon', 'Enewetak', 'Jabat',
    'Jaluit', 'Kili', 'Kwajalein', 'Lae', 'Lib', 'Likiep', 'Majuro', 'Maloelap',
    'Mejit', 'Mili', 'Namdrik', 'Namu', 'Rongelap', 'Rongrik', 'Toke', 'Ujae',
    'Ujelang', 'Utirik', 'Wotho', 'Wotje'
  ]},
  { code: 'PW', name: 'Palau', states: [
    'Aimeliik', 'Airai', 'Angaur', 'Hatohobei', 'Kayangel', 'Koror', 'Melekeok',
    'Ngaraard', 'Ngarchelong', 'Ngardmau', 'Ngatpang', 'Ngchesar', 'Ngeremlengui',
    'Ngiwal', 'Peleliu', 'Sonsorol'
  ]},
  { code: 'TO', name: 'Tonga', states: [
    'Eua', 'Ha\'apai', 'Niuas', 'Tongatapu', 'Vava\'u'
  ]},
  { code: 'VU', name: 'Vanuatu', states: [
    'Malampa', 'Penama', 'Sanma', 'Shefa', 'Tafea', 'Torba'
  ]},
  { code: 'PG', name: 'Papua New Guinea', states: [
    'Bougainville', 'Central', 'Chimbu', 'Eastern Highlands', 'East New Britain',
    'East Sepik', 'Enga', 'Gulf', 'Hela', 'Jiwaka', 'Madang', 'Manus', 'Milne Bay',
    'Morobe', 'National Capital District', 'New Ireland', 'Northern', 'Southern Highlands',
    'Western', 'Western Highlands', 'West New Britain', 'West Sepik'
  ]},
  { code: 'SB', name: 'Solomon Islands', states: [
    'Central', 'Choiseul', 'Guadalcanal', 'Honiara', 'Isabel', 'Makira-Ulawa',
    'Malaita', 'Rennell and Bellona', 'Temotu', 'Western'
  ]},
  { code: 'NC', name: 'New Caledonia', states: [
    'Loyalty Islands', 'North', 'South'
  ]},
  { code: 'PF', name: 'French Polynesia', states: [
    'Austral Islands', 'Gambier Islands', 'Marquesas Islands', 'Society Islands', 'Tuamotu Archipelago'
  ]},
  
  // More Asian Countries
  { code: 'HK', name: 'Hong Kong', states: [
    'Central and Western', 'Eastern', 'Islands', 'Kowloon City', 'Kwai Tsing',
    'Kwun Tong', 'North', 'Sai Kung', 'Sha Tin', 'Sham Shui Po', 'Southern',
    'Tai Po', 'Tsuen Wan', 'Tuen Mun', 'Wan Chai', 'Wong Tai Sin', 'Yau Tsim Mong', 'Yuen Long'
  ]},
  { code: 'MO', name: 'Macau', states: [
    'Macau Peninsula', 'Taipa', 'Coloane'
  ]},
  { code: 'TW', name: 'Taiwan', states: [
    'Changhua', 'Chiayi', 'Hsinchu', 'Hualien', 'Kaohsiung', 'Keelung', 'Kinmen',
    'Lienchiang', 'Miaoli', 'Nantou', 'New Taipei', 'Penghu', 'Pingtung', 'Taichung',
    'Tainan', 'Taipei', 'Taitung', 'Taoyuan', 'Yilan', 'Yunlin'
  ]},
  
  // More South American Countries
  { code: 'CL', name: 'Chile', states: [
    'Aisen', 'Antofagasta', 'Araucania', 'Arica y Parinacota', 'Atacama', 'Bio-Bio',
    'Coquimbo', 'Los Lagos', 'Los Rios', 'Magallanes', 'Maule', 'Metropolitan',
    'O\'Higgins', 'Tarapaca', 'Valparaiso'
  ]},
  { code: 'CO', name: 'Colombia', states: [
    'Amazonas', 'Antioquia', 'Arauca', 'Atlantico', 'Bogota', 'Bolivar', 'Boyaca',
    'Caldas', 'Caqueta', 'Casanare', 'Cauca', 'Cesar', 'Choco', 'Cordoba',
    'Cundinamarca', 'Guainia', 'Guaviare', 'Huila', 'La Guajira', 'Magdalena',
    'Meta', 'Narino', 'Norte de Santander', 'Putumayo', 'Quindio', 'Risaralda',
    'San Andres y Providencia', 'Santander', 'Sucre', 'Tolima', 'Valle del Cauca',
    'Vaupes', 'Vichada'
  ]},
  { code: 'PE', name: 'Peru', states: [
    'Amazonas', 'Ancash', 'Apurimac', 'Arequipa', 'Ayacucho', 'Cajamarca', 'Callao',
    'Cusco', 'Huancavelica', 'Huanuco', 'Ica', 'Junin', 'La Libertad', 'Lambayeque',
    'Lima', 'Loreto', 'Madre de Dios', 'Moquegua', 'Pasco', 'Piura', 'Puno',
    'San Martin', 'Tacna', 'Tumbes', 'Ucayali'
  ]},
  { code: 'VE', name: 'Venezuela', states: [
    'Amazonas', 'Anzoategui', 'Apure', 'Aragua', 'Barinas', 'Bolivar', 'Carabobo',
    'Cojedes', 'Delta Amacuro', 'Falcon', 'Guarico', 'Lara', 'Merida', 'Miranda',
    'Monagas', 'Nueva Esparta', 'Portuguesa', 'Sucre', 'Tachira', 'Trujillo',
    'Vargas', 'Yaracuy', 'Zulia', 'Capital District'
  ]},
  { code: 'EC', name: 'Ecuador', states: [
    'Azuay', 'Bolivar', 'Canar', 'Carchi', 'Chimborazo', 'Cotopaxi', 'El Oro',
    'Esmeraldas', 'Galapagos', 'Guayas', 'Imbabura', 'Loja', 'Los Rios',
    'Manabi', 'Morona-Santiago', 'Napo', 'Orellana', 'Pastaza', 'Pichincha',
    'Santa Elena', 'Santo Domingo de los Tsachilas', 'Sucumbios', 'Tungurahua',
    'Zamora-Chinchipe'
  ]},
  { code: 'UY', name: 'Uruguay', states: [
    'Artigas', 'Canelones', 'Cerro Largo', 'Colonia', 'Durazno', 'Flores',
    'Florida', 'Lavalleja', 'Maldonado', 'Montevideo', 'Paysandu', 'Rio Negro',
    'Rivera', 'Rocha', 'Salto', 'San Jose', 'Soriano', 'Tacuarembo',
    'Treinta y Tres'
  ]},
  { code: 'PY', name: 'Paraguay', states: [
    'Alto Paraguay', 'Alto Parana', 'Amambay', 'Asuncion', 'Boqueron', 'Caaguazu',
    'Caazapa', 'Canindeyu', 'Central', 'Concepcion', 'Cordillera', 'Guaira',
    'Itapua', 'Misiones', 'Neembucu', 'Paraguari', 'Presidente Hayes', 'San Pedro'
  ]},
  { code: 'BO', name: 'Bolivia', states: [
    'Beni', 'Chuquisaca', 'Cochabamba', 'La Paz', 'Oruro', 'Pando', 'Potosi',
    'Santa Cruz', 'Tarija'
  ]},
  { code: 'GY', name: 'Guyana', states: [
    'Barima-Waini', 'Cuyuni-Mazaruni', 'Demerara-Mahaica', 'East Berbice-Corentyne',
    'Essequibo Islands-West Demerara', 'Mahaica-Berbice', 'Pomeroon-Supenaam',
    'Potaro-Siparuni', 'Upper Demerara-Berbice', 'Upper Takutu-Upper Essequibo'
  ]},
  { code: 'SR', name: 'Suriname', states: [
    'Brokopondo', 'Commewijne', 'Coronie', 'Marowijne', 'Nickerie', 'Para',
    'Paramaribo', 'Saramacca', 'Sipaliwini', 'Wanica'
  ]},
  
  // More North American Countries
  { code: 'GT', name: 'Guatemala', states: [
    'Alta Verapaz', 'Baja Verapaz', 'Chimaltenango', 'Chiquimula', 'El Progreso',
    'Escuintla', 'Guatemala', 'Huehuetenango', 'Izabal', 'Jalapa', 'Jutiapa',
    'Peten', 'Quetzaltenango', 'Quiche', 'Retalhuleu', 'Sacatepequez',
    'San Marcos', 'Santa Rosa', 'Solola', 'Suchitepequez', 'Totonicapan', 'Zacapa'
  ]},
  { code: 'SV', name: 'El Salvador', states: [
    'Ahuachapan', 'Cabanas', 'Chalatenango', 'Cuscatlan', 'La Libertad', 'La Paz',
    'La Union', 'Morazan', 'San Miguel', 'San Salvador', 'San Vicente',
    'Santa Ana', 'Sonsonate', 'Usulutan'
  ]},
  { code: 'HN', name: 'Honduras', states: [
    'Atlantida', 'Choluteca', 'Colon', 'Comayagua', 'Copan', 'Cortes',
    'El Paraiso', 'Francisco Morazan', 'Gracias a Dios', 'Intibuca',
    'Islas de la Bahia', 'La Paz', 'Lempira', 'Ocotepeque', 'Olancho',
    'Santa Barbara', 'Valle', 'Yoro'
  ]},
  { code: 'NI', name: 'Nicaragua', states: [
    'Boaco', 'Carazo', 'Chinandega', 'Chontales', 'Esteli', 'Granada',
    'Jinotega', 'Leon', 'Madriz', 'Managua', 'Masaya', 'Matagalpa',
    'Nueva Segovia', 'Rio San Juan', 'Rivas', 'North Caribbean Coast',
    'South Caribbean Coast'
  ]},
  { code: 'CR', name: 'Costa Rica', states: [
    'Alajuela', 'Cartago', 'Guanacaste', 'Heredia', 'Limon', 'Puntarenas', 'San Jose'
  ]},
  { code: 'PA', name: 'Panama', states: [
    'Bocas del Toro', 'Chiriqui', 'Cocle', 'Colon', 'Darien', 'Herrera',
    'Los Santos', 'Panama', 'San Miguelito', 'Veraguas'
  ]},
  { code: 'ZW', name: 'Zimbabwe', states: [
    'Bulawayo', 'Harare', 'Manicaland', 'Mashonaland Central', 'Mashonaland East',
    'Mashonaland West', 'Masvingo', 'Matabeleland North', 'Matabeleland South', 'Midlands'
  ]},
  { code: 'OTHER', name: 'Other', states: ['Province/State/Region'] }
].sort((a, b) => a.name.localeCompare(b.name));

const SignupNewPage = () => {
  const router = useRouter();
  
  // Get Zustand store actions (same as login page)
  const handleLoginSuccess = useHandleLoginSuccessAction();
  const logStoreState = useLogStoreState();
  
  // Timeout ref for preventing infinite loading
  const timeoutRef = useRef(null);
  
  // Employment options for step 3
  const employmentOptions = [
    { value: 'employed', label: 'Employed' },
    { value: 'self-employed', label: 'Self-Employed' },
    { value: 'unemployed', label: 'Unemployed' },
    { value: 'retired', label: 'Retired' },
    { value: 'student', label: 'Student' }
  ];
  
  // State Management
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryCode: '+1',
    dateOfBirth: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US'
    },
    employmentStatus: '',
    annualIncome: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    agreeToPrivacy: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Helper function to get states for selected country
  const getStatesForCountry = (countryCode) => {
    const country = countries.find(c => c.code === countryCode);
    return country ? country.states : ['Province/State/Region'];
  };

  // Real-time validation helper
  const validateField = (name, value) => {
    switch (name) {
      case 'phone': {
        const cleanPhone = value.replace(/[\s\-\(\)\.]/g, '');
        const combinedPhone = (formData.countryCode || '+1') + cleanPhone;
        if (value && !/^\+?[1-9]\d{6,14}$/.test(combinedPhone)) {
          return 'Please enter a valid phone number (7–15 digits)';
        }
        break;
      }
      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Please enter a valid email address';
        }
        break;
      case 'password':
        if (value) {
          const hasUppercase = /[A-Z]/.test(value);
          const hasLowercase = /[a-z]/.test(value);
          const hasNumber = /\d/.test(value);
          const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?`~]/.test(value);
          
          if (value.length < 6) {
            return 'Password must be at least 6 characters long';
          }
          
          let requirementsMet = 0;
          if (hasUppercase) requirementsMet++;
          if (hasLowercase) requirementsMet++;
          if (hasNumber) requirementsMet++;
          if (hasSpecialChar) requirementsMet++;
          
          if (requirementsMet < 3) {
            return 'Password needs 3 of: uppercase, lowercase, number, special character';
          }
        }
        break;
      case 'confirmPassword':
        if (value && value !== formData.password) {
          return 'Passwords do not match';
        }
        break;
    }
    return '';
  };

  // Form handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Real-time validation
    const fieldError = validateField(name, type === 'checkbox' ? checked : value);
    setErrors(prev => ({
      ...prev,
      [name]: fieldError
    }));
  };

  // Validation functions
  const validateStep1 = () => {
    const stepErrors = {};
    
    if (!formData.firstName.trim()) {
      stepErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      stepErrors.firstName = 'First name must be at least 2 characters';
    }
    
    if (!formData.lastName.trim()) {
      stepErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      stepErrors.lastName = 'Last name must be at least 2 characters';
    }
    
    if (!formData.email.trim()) {
      stepErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      stepErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phone.trim()) {
      stepErrors.phone = 'Phone number is required';
    } else {
      const cleanPhone = formData.phone.replace(/[\s\-\(\)\.]/g, '');
      const combinedPhone = (formData.countryCode || '+1') + cleanPhone;
      if (!/^\+?[1-9]\d{6,14}$/.test(combinedPhone)) {
        stepErrors.phone = 'Please enter a valid phone number (7–15 digits)';
      }
    }
    
    if (!formData.dateOfBirth) {
      stepErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 18) {
        stepErrors.dateOfBirth = 'You must be at least 18 years old';
      }
    }
    
    return stepErrors;
  };

  const validateStep2 = () => {
    const stepErrors = {};
    
    if (!formData.address.country.trim()) {
      stepErrors['address.country'] = 'Country is required';
    }
    
    if (!formData.address.street.trim()) {
      stepErrors['address.street'] = 'Street address is required';
    } else if (formData.address.street.trim().length < 5) {
      stepErrors['address.street'] = 'Street address must be at least 5 characters';
    }
    
    if (!formData.address.city.trim()) {
      stepErrors['address.city'] = 'City is required';
    } else if (formData.address.city.trim().length < 2) {
      stepErrors['address.city'] = 'City must be at least 2 characters';
    }
    
    if (!formData.address.state.trim()) {
      stepErrors['address.state'] = 'State/Province/Region is required';
    } else if (formData.address.state.trim().length < 2) {
      stepErrors['address.state'] = 'State must be at least 2 characters';
    }
    
    if (!formData.address.zipCode.trim()) {
      stepErrors['address.zipCode'] = 'ZIP/Postal code is required';
    } else if (formData.address.zipCode.trim().length < 3) {
      stepErrors['address.zipCode'] = 'ZIP/Postal code must be at least 3 characters';
    }
    
    return stepErrors;
  };

  const validateStep3 = () => {
    const stepErrors = {};
    
    if (!formData.employmentStatus.trim()) {
      stepErrors.employmentStatus = 'Employment status is required';
    }
    
    if (!formData.annualIncome.toString().trim()) {
      stepErrors.annualIncome = 'Annual income is required';
    } else if (isNaN(formData.annualIncome) || Number(formData.annualIncome) < 0) {
      stepErrors.annualIncome = 'Please enter a valid annual income';
    }
    
    return stepErrors;
  };

  const validateStep4 = () => {
    const stepErrors = {};
    
    if (!formData.password.trim()) {
      stepErrors.password = 'Password is required';
    } else {
      const password = formData.password;
      const hasUppercase = /[A-Z]/.test(password);
      const hasLowercase = /[a-z]/.test(password);
      const hasNumber = /\d/.test(password);
      const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?`~]/.test(password);
      
      if (password.length < 6) {
        stepErrors.password = 'Password must be at least 6 characters long';
      } else {
        // Count how many requirements are met
        let requirementsMet = 0;
        if (hasUppercase) requirementsMet++;
        if (hasLowercase) requirementsMet++;
        if (hasNumber) requirementsMet++;
        if (hasSpecialChar) requirementsMet++;
        
        // Need at least 3 out of 4 requirements
        if (requirementsMet < 3) {
          stepErrors.password = 'Password must contain at least 3 of: uppercase, lowercase, number, special character';
        }
      }
    }
    
    if (!formData.confirmPassword.trim()) {
      stepErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      stepErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.agreeToTerms) {
      stepErrors.agreeToTerms = 'You must agree to the Terms and Conditions';
    }
    
    if (!formData.agreeToPrivacy) {
      stepErrors.agreeToPrivacy = 'You must agree to the Privacy Policy';
    }
    
    return stepErrors;
  };

  // Navigation handlers
  const handleNext = () => {
    let stepErrors = {};
    
    switch (currentStep) {
      case 1:
        stepErrors = validateStep1();
        break;
      case 2:
        stepErrors = validateStep2();
        break;
      case 3:
        stepErrors = validateStep3();
        break;
      default:
        break;
    }
    
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    
    setErrors({});
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const stepErrors = validateStep4();
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    
    setIsLoading(true);
    setErrors({});
    
    // Set a timeout to prevent infinite loading (20 seconds)
    timeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      setErrors({ general: 'Request timed out. Please check your connection and try again.' });
    }, 20000);
    
    try {
      console.log('🚀 SIGNUP: Starting registration...');
      
      // Transform form data for API
      const cleanPhone = formData.phone.replace(/[\s\-\(\)\.]/g, '');
      const apiData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        phone: (formData.countryCode || '+1') + cleanPhone, // Combine country code with phone (guard against undefined)
        dateOfBirth: formData.dateOfBirth,
        address: formData.address,
        employmentStatus: formData.employmentStatus,
        annualIncome: isNaN(Number(formData.annualIncome)) ? 0 : Number(formData.annualIncome), // Guard against NaN from empty input
        agreeToTerms: formData.agreeToTerms,
        agreeToPrivacy: formData.agreeToPrivacy
      };
      
      console.log('📤 SIGNUP: Sending data to API:', { ...apiData, password: '[HIDDEN]', confirmPassword: '[HIDDEN]' });
      
      // Retry logic for cold start issues
      let response;
      let retryCount = 0;
      const maxRetries = 2;
      
      while (retryCount <= maxRetries) {
        try {
          response = await fetch('/api/auth/register', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(apiData),
          });
          
          // If we get a 500 error, retry (likely cold start)
          if (response.status === 500 && retryCount < maxRetries) {
            retryCount++;
            console.log(`⚠️ SIGNUP: Server error, retrying (${retryCount}/${maxRetries})...`);
            await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
            continue;
          }
          
          break; // Success or non-retryable error
        } catch (fetchError) {
          if (retryCount < maxRetries) {
            retryCount++;
            console.log(`⚠️ SIGNUP: Network error, retrying (${retryCount}/${maxRetries})...`);
            await new Promise(resolve => setTimeout(resolve, 1000));
            continue;
          }
          throw fetchError;
        }
      }

      // Clear the timeout since we got a response
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      const data = await response.json();

      if (response.ok) {
        console.log('✅ SIGNUP: Registration successful, setting up auth state...');
        
        // Use handleLoginSuccess to properly set Zustand state (same as login page)
        if (handleLoginSuccess) {
          console.log('🔐 SIGNUP: Calling handleLoginSuccess with user data...');
          await handleLoginSuccess({
            user: data.user,
            token: data.token
          });
          
          // Allow time for state to persist before redirect (same as login page)
          setTimeout(() => {
            console.log('💾 SIGNUP: State persisted, redirecting to dashboard...');
            logStoreState('SIGNUP_SUCCESS');
            router.push('/dashboard');
          }, 300);
        } else {
          console.warn('⚠️ SIGNUP: handleLoginSuccess not available, using fallback redirect');
          router.push('/dashboard');
        }
      } else {
        // Log the full server response so we can debug the exact validation failure
        console.error('❌ SIGNUP: Server returned error:', JSON.stringify(data, null, 2));
        console.error('❌ SIGNUP: HTTP status:', response.status);
        if (data.errors) {
          console.error('❌ SIGNUP: Validation errors:', data.errors);
          const fieldErrors = {};
          data.errors.forEach(error => {
            fieldErrors[error.field] = error.message;
          });
          setErrors(fieldErrors);
        } else {
          console.error('❌ SIGNUP: Error message:', data.message);
          setErrors({ general: data.message || 'Registration failed' });
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error('❌ SIGNUP: Error during registration:', error);
      
      // Clear the timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      setErrors({ general: 'Network error. Please check your connection and try again.' });
      setIsLoading(false);
    }
  };
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Step Components
  const renderStep1 = () => (
    <div data-component-id="src/app/signup/page.jsx:2226:4" data-component-path="src/app/signup/page.jsx" data-component-line="2226" data-component-end-line="2336" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
      <div data-component-id="src/app/signup/page.jsx:2227:6" data-component-path="src/app/signup/page.jsx" data-component-line="2227" data-component-end-line="2265" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* First Name */}
        <div data-component-id="src/app/signup/page.jsx:2229:8" data-component-path="src/app/signup/page.jsx" data-component-line="2229" data-component-end-line="2245" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <label data-component-id="src/app/signup/page.jsx:2230:10" data-component-path="src/app/signup/page.jsx" data-component-line="2230" data-component-end-line="2232" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-white%20mb-2%22%2C%22textContent%22%3A%22First%20Name%20*%22%7D" htmlFor="firstName" className="block text-sm font-medium text-white mb-2">
            First Name *
          </label>
          <input data-component-id="src/app/signup/page.jsx:2233:10" data-component-path="src/app/signup/page.jsx" data-component-line="2233" data-component-end-line="2243" data-component-file="page.jsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22id%22%3A%22firstName%22%2C%22name%22%3A%22firstName%22%7D"
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1d9b5d] focus:border-transparent transition-colors ${
              errors.firstName ? 'border-red-500' : 'border-white/30'
            }`}
            placeholder="Enter your first name"
          />
          {errors.firstName && <p data-component-id="src/app/signup/page.jsx:2244:31" data-component-path="src/app/signup/page.jsx" data-component-line="2244" data-component-end-line="2244" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors.firstName}</p>}
        </div>

        {/* Last Name */}
        <div data-component-id="src/app/signup/page.jsx:2248:8" data-component-path="src/app/signup/page.jsx" data-component-line="2248" data-component-end-line="2264" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <label data-component-id="src/app/signup/page.jsx:2249:10" data-component-path="src/app/signup/page.jsx" data-component-line="2249" data-component-end-line="2251" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-white%20mb-2%22%2C%22textContent%22%3A%22Last%20Name%20*%22%7D" htmlFor="lastName" className="block text-sm font-medium text-white mb-2">
            Last Name *
          </label>
          <input data-component-id="src/app/signup/page.jsx:2252:10" data-component-path="src/app/signup/page.jsx" data-component-line="2252" data-component-end-line="2262" data-component-file="page.jsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22id%22%3A%22lastName%22%2C%22name%22%3A%22lastName%22%7D"
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1d9b5d] focus:border-transparent transition-colors ${
              errors.lastName ? 'border-red-500' : 'border-white/30'
            }`}
            placeholder="Enter your last name"
          />
          {errors.lastName && <p data-component-id="src/app/signup/page.jsx:2263:30" data-component-path="src/app/signup/page.jsx" data-component-line="2263" data-component-end-line="2263" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors.lastName}</p>}
        </div>
      </div>

      {/* Email */}
      <div data-component-id="src/app/signup/page.jsx:2268:6" data-component-path="src/app/signup/page.jsx" data-component-line="2268" data-component-end-line="2284" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <label data-component-id="src/app/signup/page.jsx:2269:8" data-component-path="src/app/signup/page.jsx" data-component-line="2269" data-component-end-line="2271" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-white%20mb-2%22%2C%22textContent%22%3A%22Email%20Address%20*%22%7D" htmlFor="email" className="block text-sm font-medium text-white mb-2">
          Email Address *
        </label>
        <input data-component-id="src/app/signup/page.jsx:2272:8" data-component-path="src/app/signup/page.jsx" data-component-line="2272" data-component-end-line="2282" data-component-file="page.jsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22email%22%2C%22id%22%3A%22email%22%2C%22name%22%3A%22email%22%7D"
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1d9b5d] focus:border-transparent transition-colors ${
            errors.email ? 'border-red-500' : 'border-white/30'
          }`}
          placeholder="Enter your email address"
        />
        {errors.email && <p data-component-id="src/app/signup/page.jsx:2283:25" data-component-path="src/app/signup/page.jsx" data-component-line="2283" data-component-end-line="2283" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors.email}</p>}
      </div>

      {/* Phone */}
      <div data-component-id="src/app/signup/page.jsx:2287:6" data-component-path="src/app/signup/page.jsx" data-component-line="2287" data-component-end-line="2317" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <label data-component-id="src/app/signup/page.jsx:2288:8" data-component-path="src/app/signup/page.jsx" data-component-line="2288" data-component-end-line="2290" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-white%20mb-2%22%2C%22textContent%22%3A%22Phone%20Number%20*%22%7D" htmlFor="phone" className="block text-sm font-medium text-white mb-2">
          Phone Number *
        </label>
        <div data-component-id="src/app/signup/page.jsx:2291:8" data-component-path="src/app/signup/page.jsx" data-component-line="2291" data-component-end-line="2315" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-3%22%7D" className="flex gap-3">
          <select data-component-id="src/app/signup/page.jsx:2292:10" data-component-path="src/app/signup/page.jsx" data-component-line="2292" data-component-end-line="2303" data-component-file="page.jsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22name%22%3A%22countryCode%22%2C%22className%22%3A%22px-3%20py-3%20bg-white%2F10%20border%20border-white%2F30%20rounded-lg%20text-white%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-%5B%231d9b5d%5D%20focus%3Aborder-transparent%22%7D"
            name="countryCode"
            value={formData.countryCode}
            onChange={handleChange}
            className="px-3 py-3 bg-white/10 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#1d9b5d] focus:border-transparent"
          >
            {countryCodes.map(country => (
              <option data-component-id="src/app/signup/page.jsx:2299:14" data-component-path="src/app/signup/page.jsx" data-component-line="2299" data-component-end-line="2301" data-component-file="page.jsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22className%22%3A%22bg-gray-800%22%7D" key={country.code} value={country.code} className="bg-gray-800">
                {country.flag} {country.code}
              </option>
            ))}
          </select>
          <input data-component-id="src/app/signup/page.jsx:2304:10" data-component-path="src/app/signup/page.jsx" data-component-line="2304" data-component-end-line="2314" data-component-file="page.jsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22tel%22%2C%22id%22%3A%22phone%22%2C%22name%22%3A%22phone%22%7D"
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`flex-1 px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1d9b5d] focus:border-transparent transition-colors ${
              errors.phone ? 'border-red-500' : 'border-white/30'
            }`}
            placeholder="Enter your phone number"
          />
        </div>
        {errors.phone && <p data-component-id="src/app/signup/page.jsx:2316:25" data-component-path="src/app/signup/page.jsx" data-component-line="2316" data-component-end-line="2316" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors.phone}</p>}
      </div>

      {/* Date of Birth */}
      <div data-component-id="src/app/signup/page.jsx:2320:6" data-component-path="src/app/signup/page.jsx" data-component-line="2320" data-component-end-line="2335" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <label data-component-id="src/app/signup/page.jsx:2321:8" data-component-path="src/app/signup/page.jsx" data-component-line="2321" data-component-end-line="2323" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-white%20mb-2%22%2C%22textContent%22%3A%22Date%20of%20Birth%20*%22%7D" htmlFor="dateOfBirth" className="block text-sm font-medium text-white mb-2">
          Date of Birth *
        </label>
        <input data-component-id="src/app/signup/page.jsx:2324:8" data-component-path="src/app/signup/page.jsx" data-component-line="2324" data-component-end-line="2333" data-component-file="page.jsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22date%22%2C%22id%22%3A%22dateOfBirth%22%2C%22name%22%3A%22dateOfBirth%22%7D"
          type="date"
          id="dateOfBirth"
          name="dateOfBirth"
          value={formData.dateOfBirth}
          onChange={handleChange}
          className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#1d9b5d] focus:border-transparent transition-colors ${
            errors.dateOfBirth ? 'border-red-500' : 'border-white/30'
          }`}
        />
        {errors.dateOfBirth && <p data-component-id="src/app/signup/page.jsx:2334:31" data-component-path="src/app/signup/page.jsx" data-component-line="2334" data-component-end-line="2334" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors.dateOfBirth}</p>}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div data-component-id="src/app/signup/page.jsx:2340:4" data-component-path="src/app/signup/page.jsx" data-component-line="2340" data-component-end-line="2447" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
      {/* Country */}
      <div data-component-id="src/app/signup/page.jsx:2342:6" data-component-path="src/app/signup/page.jsx" data-component-line="2342" data-component-end-line="2363" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <label data-component-id="src/app/signup/page.jsx:2343:8" data-component-path="src/app/signup/page.jsx" data-component-line="2343" data-component-end-line="2345" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-white%20mb-2%22%2C%22textContent%22%3A%22Country%20*%22%7D" htmlFor="address.country" className="block text-sm font-medium text-white mb-2">
          Country *
        </label>
        <select data-component-id="src/app/signup/page.jsx:2346:8" data-component-path="src/app/signup/page.jsx" data-component-line="2346" data-component-end-line="2361" data-component-file="page.jsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22id%22%3A%22address.country%22%2C%22name%22%3A%22address.country%22%7D"
          id="address.country"
          name="address.country"
          value={formData.address.country}
          onChange={handleChange}
          className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#1d9b5d] focus:border-transparent transition-colors ${
            errors['address.country'] ? 'border-red-500' : 'border-white/30'
          }`}
        >
          <option data-component-id="src/app/signup/page.jsx:2355:10" data-component-path="src/app/signup/page.jsx" data-component-line="2355" data-component-end-line="2355" data-component-file="page.jsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22%22%2C%22className%22%3A%22bg-gray-800%22%2C%22textContent%22%3A%22Select%20Country%22%7D" value="" className="bg-gray-800">Select Country</option>
          {countries.map(country => (
            <option data-component-id="src/app/signup/page.jsx:2357:12" data-component-path="src/app/signup/page.jsx" data-component-line="2357" data-component-end-line="2359" data-component-file="page.jsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22className%22%3A%22bg-gray-800%22%7D" key={country.code} value={country.code} className="bg-gray-800">
              {country.name}
            </option>
          ))}
        </select>
        {errors['address.country'] && <p data-component-id="src/app/signup/page.jsx:2362:38" data-component-path="src/app/signup/page.jsx" data-component-line="2362" data-component-end-line="2362" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors['address.country']}</p>}
      </div>

      {/* Street Address */}
      <div data-component-id="src/app/signup/page.jsx:2366:6" data-component-path="src/app/signup/page.jsx" data-component-line="2366" data-component-end-line="2382" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <label data-component-id="src/app/signup/page.jsx:2367:8" data-component-path="src/app/signup/page.jsx" data-component-line="2367" data-component-end-line="2369" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-white%20mb-2%22%2C%22textContent%22%3A%22Street%20Address%20*%22%7D" htmlFor="address.street" className="block text-sm font-medium text-white mb-2">
          Street Address *
        </label>
        <input data-component-id="src/app/signup/page.jsx:2370:8" data-component-path="src/app/signup/page.jsx" data-component-line="2370" data-component-end-line="2380" data-component-file="page.jsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22id%22%3A%22address.street%22%2C%22name%22%3A%22address.street%22%7D"
          type="text"
          id="address.street"
          name="address.street"
          value={formData.address.street}
          onChange={handleChange}
          className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1d9b5d] focus:border-transparent transition-colors ${
            errors['address.street'] ? 'border-red-500' : 'border-white/30'
          }`}
          placeholder="Enter your street address"
        />
        {errors['address.street'] && <p data-component-id="src/app/signup/page.jsx:2381:37" data-component-path="src/app/signup/page.jsx" data-component-line="2381" data-component-end-line="2381" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors['address.street']}</p>}
      </div>

      <div data-component-id="src/app/signup/page.jsx:2384:6" data-component-path="src/app/signup/page.jsx" data-component-line="2384" data-component-end-line="2427" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* City */}
        <div data-component-id="src/app/signup/page.jsx:2386:8" data-component-path="src/app/signup/page.jsx" data-component-line="2386" data-component-end-line="2402" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <label data-component-id="src/app/signup/page.jsx:2387:10" data-component-path="src/app/signup/page.jsx" data-component-line="2387" data-component-end-line="2389" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-white%20mb-2%22%2C%22textContent%22%3A%22City%20*%22%7D" htmlFor="address.city" className="block text-sm font-medium text-white mb-2">
            City *
          </label>
          <input data-component-id="src/app/signup/page.jsx:2390:10" data-component-path="src/app/signup/page.jsx" data-component-line="2390" data-component-end-line="2400" data-component-file="page.jsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22id%22%3A%22address.city%22%2C%22name%22%3A%22address.city%22%7D"
            type="text"
            id="address.city"
            name="address.city"
            value={formData.address.city}
            onChange={handleChange}
            className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1d9b5d] focus:border-transparent transition-colors ${
              errors['address.city'] ? 'border-red-500' : 'border-white/30'
            }`}
            placeholder="Enter your city"
          />
          {errors['address.city'] && <p data-component-id="src/app/signup/page.jsx:2401:37" data-component-path="src/app/signup/page.jsx" data-component-line="2401" data-component-end-line="2401" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors['address.city']}</p>}
        </div>

        {/* State/Province */}
        <div data-component-id="src/app/signup/page.jsx:2405:8" data-component-path="src/app/signup/page.jsx" data-component-line="2405" data-component-end-line="2426" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <label data-component-id="src/app/signup/page.jsx:2406:10" data-component-path="src/app/signup/page.jsx" data-component-line="2406" data-component-end-line="2408" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-white%20mb-2%22%2C%22textContent%22%3A%22State%2FProvince%20*%22%7D" htmlFor="address.state" className="block text-sm font-medium text-white mb-2">
            State/Province *
          </label>
          <select data-component-id="src/app/signup/page.jsx:2409:10" data-component-path="src/app/signup/page.jsx" data-component-line="2409" data-component-end-line="2424" data-component-file="page.jsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22id%22%3A%22address.state%22%2C%22name%22%3A%22address.state%22%7D"
            id="address.state"
            name="address.state"
            value={formData.address.state}
            onChange={handleChange}
            className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#1d9b5d] focus:border-transparent transition-colors ${
              errors['address.state'] ? 'border-red-500' : 'border-white/30'
            }`}
          >
            <option data-component-id="src/app/signup/page.jsx:2418:12" data-component-path="src/app/signup/page.jsx" data-component-line="2418" data-component-end-line="2418" data-component-file="page.jsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22%22%2C%22className%22%3A%22bg-gray-800%22%2C%22textContent%22%3A%22Select%20State%2FProvince%22%7D" value="" className="bg-gray-800">Select State/Province</option>
            {getStatesForCountry(formData.address.country).map(state => (
              <option data-component-id="src/app/signup/page.jsx:2420:14" data-component-path="src/app/signup/page.jsx" data-component-line="2420" data-component-end-line="2422" data-component-file="page.jsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22%5Bvar%3Astate%5D%22%2C%22className%22%3A%22bg-gray-800%22%7D" key={state} value={state} className="bg-gray-800">
                {state}
              </option>
            ))}
          </select>
          {errors['address.state'] && <p data-component-id="src/app/signup/page.jsx:2425:38" data-component-path="src/app/signup/page.jsx" data-component-line="2425" data-component-end-line="2425" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors['address.state']}</p>}
        </div>
      </div>

      {/* ZIP/Postal Code */}
      <div data-component-id="src/app/signup/page.jsx:2430:6" data-component-path="src/app/signup/page.jsx" data-component-line="2430" data-component-end-line="2446" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <label data-component-id="src/app/signup/page.jsx:2431:8" data-component-path="src/app/signup/page.jsx" data-component-line="2431" data-component-end-line="2433" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-white%20mb-2%22%2C%22textContent%22%3A%22ZIP%2FPostal%20Code%20*%22%7D" htmlFor="address.zipCode" className="block text-sm font-medium text-white mb-2">
          ZIP/Postal Code *
        </label>
        <input data-component-id="src/app/signup/page.jsx:2434:8" data-component-path="src/app/signup/page.jsx" data-component-line="2434" data-component-end-line="2444" data-component-file="page.jsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22id%22%3A%22address.zipCode%22%2C%22name%22%3A%22address.zipCode%22%7D"
          type="text"
          id="address.zipCode"
          name="address.zipCode"
          value={formData.address.zipCode}
          onChange={handleChange}
          className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1d9b5d] focus:border-transparent transition-colors ${
            errors['address.zipCode'] ? 'border-red-500' : 'border-white/30'
          }`}
          placeholder="Enter your ZIP/postal code"
        />
        {errors['address.zipCode'] && <p data-component-id="src/app/signup/page.jsx:2445:38" data-component-path="src/app/signup/page.jsx" data-component-line="2445" data-component-end-line="2445" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors['address.zipCode']}</p>}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div data-component-id="src/app/signup/page.jsx:2451:4" data-component-path="src/app/signup/page.jsx" data-component-line="2451" data-component-end-line="2495" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
      {/* Employment Status */}
      <div data-component-id="src/app/signup/page.jsx:2453:6" data-component-path="src/app/signup/page.jsx" data-component-line="2453" data-component-end-line="2474" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <label data-component-id="src/app/signup/page.jsx:2454:8" data-component-path="src/app/signup/page.jsx" data-component-line="2454" data-component-end-line="2456" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-white%20mb-2%22%2C%22textContent%22%3A%22Employment%20Status%20*%22%7D" htmlFor="employmentStatus" className="block text-sm font-medium text-white mb-2">
          Employment Status *
        </label>
        <select data-component-id="src/app/signup/page.jsx:2457:8" data-component-path="src/app/signup/page.jsx" data-component-line="2457" data-component-end-line="2472" data-component-file="page.jsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22id%22%3A%22employmentStatus%22%2C%22name%22%3A%22employmentStatus%22%7D"
          id="employmentStatus"
          name="employmentStatus"
          value={formData.employmentStatus}
          onChange={handleChange}
          className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#1d9b5d] focus:border-transparent transition-colors ${
            errors.employmentStatus ? 'border-red-500' : 'border-white/30'
          }`}
        >
          <option data-component-id="src/app/signup/page.jsx:2466:10" data-component-path="src/app/signup/page.jsx" data-component-line="2466" data-component-end-line="2466" data-component-file="page.jsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22%22%2C%22className%22%3A%22bg-gray-800%22%2C%22textContent%22%3A%22Select%20Employment%20Status%22%7D" value="" className="bg-gray-800">Select Employment Status</option>
          <option data-component-id="src/app/signup/page.jsx:2467:10" data-component-path="src/app/signup/page.jsx" data-component-line="2467" data-component-end-line="2467" data-component-file="page.jsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22employed%22%2C%22className%22%3A%22bg-gray-800%22%2C%22textContent%22%3A%22Employed%22%7D" value="employed" className="bg-gray-800">Employed</option>
          <option data-component-id="src/app/signup/page.jsx:2468:10" data-component-path="src/app/signup/page.jsx" data-component-line="2468" data-component-end-line="2468" data-component-file="page.jsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22self-employed%22%2C%22className%22%3A%22bg-gray-800%22%2C%22textContent%22%3A%22Self-Employed%22%7D" value="self-employed" className="bg-gray-800">Self-Employed</option>
          <option data-component-id="src/app/signup/page.jsx:2469:10" data-component-path="src/app/signup/page.jsx" data-component-line="2469" data-component-end-line="2469" data-component-file="page.jsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22unemployed%22%2C%22className%22%3A%22bg-gray-800%22%2C%22textContent%22%3A%22Unemployed%22%7D" value="unemployed" className="bg-gray-800">Unemployed</option>
          <option data-component-id="src/app/signup/page.jsx:2470:10" data-component-path="src/app/signup/page.jsx" data-component-line="2470" data-component-end-line="2470" data-component-file="page.jsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22retired%22%2C%22className%22%3A%22bg-gray-800%22%2C%22textContent%22%3A%22Retired%22%7D" value="retired" className="bg-gray-800">Retired</option>
          <option data-component-id="src/app/signup/page.jsx:2471:10" data-component-path="src/app/signup/page.jsx" data-component-line="2471" data-component-end-line="2471" data-component-file="page.jsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22student%22%2C%22className%22%3A%22bg-gray-800%22%2C%22textContent%22%3A%22Student%22%7D" value="student" className="bg-gray-800">Student</option>
        </select>
        {errors.employmentStatus && <p data-component-id="src/app/signup/page.jsx:2473:36" data-component-path="src/app/signup/page.jsx" data-component-line="2473" data-component-end-line="2473" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors.employmentStatus}</p>}
      </div>

      {/* Annual Income */}
      <div data-component-id="src/app/signup/page.jsx:2477:6" data-component-path="src/app/signup/page.jsx" data-component-line="2477" data-component-end-line="2494" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <label data-component-id="src/app/signup/page.jsx:2478:8" data-component-path="src/app/signup/page.jsx" data-component-line="2478" data-component-end-line="2480" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-white%20mb-2%22%2C%22textContent%22%3A%22Annual%20Income%20(USD)%20*%22%7D" htmlFor="annualIncome" className="block text-sm font-medium text-white mb-2">
          Annual Income (USD) *
        </label>
        <input data-component-id="src/app/signup/page.jsx:2481:8" data-component-path="src/app/signup/page.jsx" data-component-line="2481" data-component-end-line="2492" data-component-file="page.jsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22number%22%2C%22id%22%3A%22annualIncome%22%2C%22name%22%3A%22annualIncome%22%7D"
          type="number"
          id="annualIncome"
          name="annualIncome"
          value={formData.annualIncome}
          onChange={handleChange}
          className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1d9b5d] focus:border-transparent transition-colors ${
            errors.annualIncome ? 'border-red-500' : 'border-white/30'
          }`}
          placeholder="Enter your annual income"
          min="0"
        />
        {errors.annualIncome && <p data-component-id="src/app/signup/page.jsx:2493:32" data-component-path="src/app/signup/page.jsx" data-component-line="2493" data-component-end-line="2493" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors.annualIncome}</p>}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div data-component-id="src/app/signup/page.jsx:2499:4" data-component-path="src/app/signup/page.jsx" data-component-line="2499" data-component-end-line="2599" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
      {/* Password */}
      <div data-component-id="src/app/signup/page.jsx:2501:6" data-component-path="src/app/signup/page.jsx" data-component-line="2501" data-component-end-line="2538" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <label data-component-id="src/app/signup/page.jsx:2502:8" data-component-path="src/app/signup/page.jsx" data-component-line="2502" data-component-end-line="2504" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-white%20mb-2%22%2C%22textContent%22%3A%22Password%20*%22%7D" htmlFor="password" className="block text-sm font-medium text-white mb-2">
          Password *
        </label>
        <div data-component-id="src/app/signup/page.jsx:2505:8" data-component-path="src/app/signup/page.jsx" data-component-line="2505" data-component-end-line="2533" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">
          <input data-component-id="src/app/signup/page.jsx:2506:10" data-component-path="src/app/signup/page.jsx" data-component-line="2506" data-component-end-line="2516" data-component-file="page.jsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22id%22%3A%22password%22%2C%22name%22%3A%22password%22%7D"
            type={showPassword ? "text" : "password"}
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1d9b5d] focus:border-transparent transition-colors ${
              errors.password ? 'border-red-500' : 'border-white/30'
            }`}
            placeholder="Create a strong password"
          />
          <button data-component-id="src/app/signup/page.jsx:2517:10" data-component-path="src/app/signup/page.jsx" data-component-line="2517" data-component-end-line="2532" data-component-file="page.jsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22absolute%20right-3%20top-1%2F2%20transform%20-translate-y-1%2F2%20text-gray-400%20hover%3Atext-white%20transition-colors%22%7D"
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
          >
            {showPassword ? (
              <svg data-component-id="src/app/signup/page.jsx:2523:14" data-component-path="src/app/signup/page.jsx" data-component-line="2523" data-component-end-line="2525" data-component-file="page.jsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path data-component-id="src/app/signup/page.jsx:2524:16" data-component-path="src/app/signup/page.jsx" data-component-line="2524" data-component-end-line="2524" data-component-file="page.jsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
              </svg>
            ) : (
              <svg data-component-id="src/app/signup/page.jsx:2527:14" data-component-path="src/app/signup/page.jsx" data-component-line="2527" data-component-end-line="2530" data-component-file="page.jsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path data-component-id="src/app/signup/page.jsx:2528:16" data-component-path="src/app/signup/page.jsx" data-component-line="2528" data-component-end-line="2528" data-component-file="page.jsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path data-component-id="src/app/signup/page.jsx:2529:16" data-component-path="src/app/signup/page.jsx" data-component-line="2529" data-component-end-line="2529" data-component-file="page.jsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>
        {errors.password && <p data-component-id="src/app/signup/page.jsx:2534:28" data-component-path="src/app/signup/page.jsx" data-component-line="2534" data-component-end-line="2534" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors.password}</p>}
        <p data-component-id="src/app/signup/page.jsx:2535:8" data-component-path="src/app/signup/page.jsx" data-component-line="2535" data-component-end-line="2537" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-xs%20text-gray-400%22%2C%22textContent%22%3A%22Password%20must%20be%20at%20least%208%20characters%20with%20uppercase%2C%20lowercase%2C%20number%2C%20and%20special%20character%22%7D" className="mt-1 text-xs text-gray-400">
          Password must be at least 8 characters with uppercase, lowercase, number, and special character
        </p>
      </div>

      {/* Confirm Password */}
      <div data-component-id="src/app/signup/page.jsx:2541:6" data-component-path="src/app/signup/page.jsx" data-component-line="2541" data-component-end-line="2557" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <label data-component-id="src/app/signup/page.jsx:2542:8" data-component-path="src/app/signup/page.jsx" data-component-line="2542" data-component-end-line="2544" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-white%20mb-2%22%2C%22textContent%22%3A%22Confirm%20Password%20*%22%7D" htmlFor="confirmPassword" className="block text-sm font-medium text-white mb-2">
          Confirm Password *
        </label>
        <input data-component-id="src/app/signup/page.jsx:2545:8" data-component-path="src/app/signup/page.jsx" data-component-line="2545" data-component-end-line="2555" data-component-file="page.jsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22password%22%2C%22id%22%3A%22confirmPassword%22%2C%22name%22%3A%22confirmPassword%22%7D"
          type="password"
          id="confirmPassword"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1d9b5d] focus:border-transparent transition-colors ${
            errors.confirmPassword ? 'border-red-500' : 'border-white/30'
          }`}
          placeholder="Confirm your password"
        />
        {errors.confirmPassword && <p data-component-id="src/app/signup/page.jsx:2556:35" data-component-path="src/app/signup/page.jsx" data-component-line="2556" data-component-end-line="2556" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>}
      </div>

      {/* Terms and Privacy */}
      <div data-component-id="src/app/signup/page.jsx:2560:6" data-component-path="src/app/signup/page.jsx" data-component-line="2560" data-component-end-line="2598" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
        <div data-component-id="src/app/signup/page.jsx:2561:8" data-component-path="src/app/signup/page.jsx" data-component-line="2561" data-component-end-line="2578" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <label data-component-id="src/app/signup/page.jsx:2562:10" data-component-path="src/app/signup/page.jsx" data-component-line="2562" data-component-end-line="2576" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22flex%20items-start%22%7D" className="flex items-start">
            <input data-component-id="src/app/signup/page.jsx:2563:12" data-component-path="src/app/signup/page.jsx" data-component-line="2563" data-component-end-line="2569" data-component-file="page.jsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22checkbox%22%2C%22name%22%3A%22agreeToTerms%22%2C%22className%22%3A%22w-4%20h-4%20mt-1%20text-%5B%231d9b5d%5D%20bg-white%2F10%20border-white%2F30%20rounded%20focus%3Aring-%5B%231d9b5d%5D%20focus%3Aring-2%22%7D"
              type="checkbox"
              name="agreeToTerms"
              checked={formData.agreeToTerms}
              onChange={handleChange}
              className="w-4 h-4 mt-1 text-[#1d9b5d] bg-white/10 border-white/30 rounded focus:ring-[#1d9b5d] focus:ring-2"
            />
            <span data-component-id="src/app/signup/page.jsx:2570:12" data-component-path="src/app/signup/page.jsx" data-component-line="2570" data-component-end-line="2575" data-component-file="page.jsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22ml-3%20text-sm%20text-gray-300%22%2C%22textContent%22%3A%22I%20agree%20to%20the%22%7D" className="ml-3 text-sm text-gray-300">
              I agree to the{' '}
              <Link data-component-id="src/app/signup/page.jsx:2572:14" data-component-path="src/app/signup/page.jsx" data-component-line="2572" data-component-end-line="2574" data-component-file="page.jsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fterms%22%2C%22className%22%3A%22text-%5B%231d9b5d%5D%20hover%3Atext-%5B%2316844f%5D%20transition-colors%22%2C%22textContent%22%3A%22Terms%20and%20Conditions%22%7D" href="/terms" className="text-[#1d9b5d] hover:text-[#16844f] transition-colors">
                Terms and Conditions
              </Link>
            </span>
          </label>
          {errors.agreeToTerms && <p data-component-id="src/app/signup/page.jsx:2577:34" data-component-path="src/app/signup/page.jsx" data-component-line="2577" data-component-end-line="2577" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors.agreeToTerms}</p>}
        </div>

        <div data-component-id="src/app/signup/page.jsx:2580:8" data-component-path="src/app/signup/page.jsx" data-component-line="2580" data-component-end-line="2597" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <label data-component-id="src/app/signup/page.jsx:2581:10" data-component-path="src/app/signup/page.jsx" data-component-line="2581" data-component-end-line="2595" data-component-file="page.jsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22flex%20items-start%22%7D" className="flex items-start">
            <input data-component-id="src/app/signup/page.jsx:2582:12" data-component-path="src/app/signup/page.jsx" data-component-line="2582" data-component-end-line="2588" data-component-file="page.jsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22checkbox%22%2C%22name%22%3A%22agreeToPrivacy%22%2C%22className%22%3A%22w-4%20h-4%20mt-1%20text-%5B%231d9b5d%5D%20bg-white%2F10%20border-white%2F30%20rounded%20focus%3Aring-%5B%231d9b5d%5D%20focus%3Aring-2%22%7D"
              type="checkbox"
              name="agreeToPrivacy"
              checked={formData.agreeToPrivacy}
              onChange={handleChange}
              className="w-4 h-4 mt-1 text-[#1d9b5d] bg-white/10 border-white/30 rounded focus:ring-[#1d9b5d] focus:ring-2"
            />
            <span data-component-id="src/app/signup/page.jsx:2589:12" data-component-path="src/app/signup/page.jsx" data-component-line="2589" data-component-end-line="2594" data-component-file="page.jsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22ml-3%20text-sm%20text-gray-300%22%2C%22textContent%22%3A%22I%20agree%20to%20the%22%7D" className="ml-3 text-sm text-gray-300">
              I agree to the{' '}
              <Link data-component-id="src/app/signup/page.jsx:2591:14" data-component-path="src/app/signup/page.jsx" data-component-line="2591" data-component-end-line="2593" data-component-file="page.jsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fprivacy%22%2C%22className%22%3A%22text-%5B%231d9b5d%5D%20hover%3Atext-%5B%2316844f%5D%20transition-colors%22%2C%22textContent%22%3A%22Privacy%20Policy%22%7D" href="/privacy" className="text-[#1d9b5d] hover:text-[#16844f] transition-colors">
                Privacy Policy
              </Link>
            </span>
          </label>
          {errors.agreeToPrivacy && <p data-component-id="src/app/signup/page.jsx:2596:36" data-component-path="src/app/signup/page.jsx" data-component-line="2596" data-component-end-line="2596" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-400%22%7D" className="mt-1 text-sm text-red-400">{errors.agreeToPrivacy}</p>}
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      default: return null;
    }
  };

  return (
    <div data-component-id="src/app/signup/page.jsx:2613:4" data-component-path="src/app/signup/page.jsx" data-component-line="2613" data-component-end-line="2727" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-gray-900%20flex%20items-center%20justify-center%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="min-h-screen bg-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8" 
         style={{ backgroundImage: 'url(/BG.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
      {/* Background overlay */}
      <div data-component-id="src/app/signup/page.jsx:2616:6" data-component-path="src/app/signup/page.jsx" data-component-line="2616" data-component-end-line="2616" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-black%2F40%22%7D" className="absolute inset-0 bg-black/40"></div>
      
      <div data-component-id="src/app/signup/page.jsx:2618:6" data-component-path="src/app/signup/page.jsx" data-component-line="2618" data-component-end-line="2726" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20w-full%20max-w-2xl%22%7D" className="relative w-full max-w-2xl">
        {/* Header */}
        <div data-component-id="src/app/signup/page.jsx:2620:8" data-component-path="src/app/signup/page.jsx" data-component-line="2620" data-component-end-line="2626" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-8%22%7D" className="text-center mb-8">
          <Link data-component-id="src/app/signup/page.jsx:2621:10" data-component-path="src/app/signup/page.jsx" data-component-line="2621" data-component-end-line="2623" data-component-file="page.jsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2F%22%2C%22className%22%3A%22inline-block%22%7D" href="/" className="inline-block">
            <img data-component-id="src/app/signup/page.jsx:2622:12" data-component-path="src/app/signup/page.jsx" data-component-line="2622" data-component-end-line="2622" data-component-file="page.jsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%2Ficon.png%22%2C%22alt%22%3A%22Vernin%20Union%22%2C%22className%22%3A%22h-10%20w-auto%20mb-2%22%7D" src="/icon.png" alt="Vernin Union" className="h-10 w-auto mb-2" />
          </Link>
          <p data-component-id="src/app/signup/page.jsx:2624:10" data-component-path="src/app/signup/page.jsx" data-component-line="2624" data-component-end-line="2624" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-gray-300%20text-lg%22%2C%22textContent%22%3A%22Create%20your%20account%22%7D" className="text-gray-300 text-lg">Create your account</p>
          <p data-component-id="src/app/signup/page.jsx:2625:10" data-component-path="src/app/signup/page.jsx" data-component-line="2625" data-component-end-line="2625" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-gray-400%20text-sm%20mt-1%22%2C%22textContent%22%3A%22Join%20thousands%20of%20satisfied%20customers%22%7D" className="text-gray-400 text-sm mt-1">Join thousands of satisfied customers</p>
        </div>

        {/* Step Progress */}
        <div data-component-id="src/app/signup/page.jsx:2629:8" data-component-path="src/app/signup/page.jsx" data-component-line="2629" data-component-end-line="2652" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
          <div data-component-id="src/app/signup/page.jsx:2630:10" data-component-path="src/app/signup/page.jsx" data-component-line="2630" data-component-end-line="2645" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-between%20items-center%22%7D" className="flex justify-between items-center">
            {[1, 2, 3, 4].map((step) => (
              <div data-component-id="src/app/signup/page.jsx:2632:14" data-component-path="src/app/signup/page.jsx" data-component-line="2632" data-component-end-line="2643" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%22%7D" key={step} className="flex items-center">
                <div data-component-id="src/app/signup/page.jsx:2633:16" data-component-path="src/app/signup/page.jsx" data-component-line="2633" data-component-end-line="2637" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= step ? 'bg-[#1d9b5d] text-white' : 'bg-white/20 text-gray-400'
                }`}>
                  {step}
                </div>
                {step < 4 && (
                  <div data-component-id="src/app/signup/page.jsx:2639:18" data-component-path="src/app/signup/page.jsx" data-component-line="2639" data-component-end-line="2641" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`w-16 h-0.5 mx-2 ${
                    currentStep > step ? 'bg-[#1d9b5d]' : 'bg-white/20'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
          <div data-component-id="src/app/signup/page.jsx:2646:10" data-component-path="src/app/signup/page.jsx" data-component-line="2646" data-component-end-line="2651" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-between%20mt-2%22%7D" className="flex justify-between mt-2">
            <span data-component-id="src/app/signup/page.jsx:2647:12" data-component-path="src/app/signup/page.jsx" data-component-line="2647" data-component-end-line="2647" data-component-file="page.jsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-gray-400%22%2C%22textContent%22%3A%22Personal%22%7D" className="text-xs text-gray-400">Personal</span>
            <span data-component-id="src/app/signup/page.jsx:2648:12" data-component-path="src/app/signup/page.jsx" data-component-line="2648" data-component-end-line="2648" data-component-file="page.jsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-gray-400%22%2C%22textContent%22%3A%22Address%22%7D" className="text-xs text-gray-400">Address</span>
            <span data-component-id="src/app/signup/page.jsx:2649:12" data-component-path="src/app/signup/page.jsx" data-component-line="2649" data-component-end-line="2649" data-component-file="page.jsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-gray-400%22%2C%22textContent%22%3A%22Financial%22%7D" className="text-xs text-gray-400">Financial</span>
            <span data-component-id="src/app/signup/page.jsx:2650:12" data-component-path="src/app/signup/page.jsx" data-component-line="2650" data-component-end-line="2650" data-component-file="page.jsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-gray-400%22%2C%22textContent%22%3A%22Security%22%7D" className="text-xs text-gray-400">Security</span>
          </div>
        </div>

        {/* Form */}
        <form data-component-id="src/app/signup/page.jsx:2655:8" data-component-path="src/app/signup/page.jsx" data-component-line="2655" data-component-end-line="2708" data-component-file="page.jsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%7D" onSubmit={handleSubmit}>
          <div data-component-id="src/app/signup/page.jsx:2656:10" data-component-path="src/app/signup/page.jsx" data-component-line="2656" data-component-end-line="2707" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%2F10%20backdrop-blur-md%20rounded-2xl%20p-8%20border%20border-white%2F20%20shadow-2xl%22%7D" className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl">
            {/* General Error */}
            {errors.general && (
              <div data-component-id="src/app/signup/page.jsx:2659:14" data-component-path="src/app/signup/page.jsx" data-component-line="2659" data-component-end-line="2661" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-red-500%2F10%20border%20border-red-500%2F20%20rounded-lg%20p-4%20mb-6%22%7D" className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
                <p data-component-id="src/app/signup/page.jsx:2660:16" data-component-path="src/app/signup/page.jsx" data-component-line="2660" data-component-end-line="2660" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-red-400%20text-sm%22%7D" className="text-red-400 text-sm">{errors.general}</p>
              </div>
            )}
            
            {/* Step Content */}
            {renderCurrentStep()}

            {/* Navigation Buttons */}
            <div data-component-id="src/app/signup/page.jsx:2668:12" data-component-path="src/app/signup/page.jsx" data-component-line="2668" data-component-end-line="2706" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-between%20mt-8%22%7D" className="flex justify-between mt-8">
              {currentStep > 1 && (
                <button data-component-id="src/app/signup/page.jsx:2670:16" data-component-path="src/app/signup/page.jsx" data-component-line="2670" data-component-end-line="2676" data-component-file="page.jsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22px-6%20py-3%20text-white%20border%20border-white%2F30%20rounded-lg%20hover%3Abg-white%2F10%20transition-colors%22%2C%22textContent%22%3A%22Previous%22%7D"
                  type="button"
                  onClick={handlePrevious}
                  className="px-6 py-3 text-white border border-white/30 rounded-lg hover:bg-white/10 transition-colors"
                >
                  Previous
                </button>
              )}
              
              {currentStep < 4 ? (
                <button data-component-id="src/app/signup/page.jsx:2680:16" data-component-path="src/app/signup/page.jsx" data-component-line="2680" data-component-end-line="2686" data-component-file="page.jsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22ml-auto%20px-6%20py-3%20bg-%5B%231d9b5d%5D%20hover%3Abg-%5B%2316844f%5D%20text-white%20rounded-lg%20transition-colors%22%2C%22textContent%22%3A%22Next%22%7D"
                  type="button"
                  onClick={handleNext}
                  className="ml-auto px-6 py-3 bg-[#1d9b5d] hover:bg-[#16844f] text-white rounded-lg transition-colors"
                >
                  Next
                </button>
              ) : (
                <button data-component-id="src/app/signup/page.jsx:2688:16" data-component-path="src/app/signup/page.jsx" data-component-line="2688" data-component-end-line="2704" data-component-file="page.jsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22submit%22%2C%22className%22%3A%22ml-auto%20px-6%20py-3%20bg-%5B%231d9b5d%5D%20hover%3Abg-%5B%2316844f%5D%20disabled%3Abg-gray-600%20text-white%20rounded-lg%20transition-colors%20flex%20items-center%22%7D"
                  type="submit"
                  disabled={isLoading}
                  className="ml-auto px-6 py-3 bg-[#1d9b5d] hover:bg-[#16844f] disabled:bg-gray-600 text-white rounded-lg transition-colors flex items-center"
                >
                  {isLoading ? (
                    <>
                      <svg data-component-id="src/app/signup/page.jsx:2695:22" data-component-path="src/app/signup/page.jsx" data-component-line="2695" data-component-end-line="2698" data-component-file="page.jsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22animate-spin%20-ml-1%20mr-3%20h-5%20w-5%20text-white%22%7D" className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle data-component-id="src/app/signup/page.jsx:2696:24" data-component-path="src/app/signup/page.jsx" data-component-line="2696" data-component-end-line="2696" data-component-file="page.jsx" data-component-name="circle" data-component-content="%7B%22elementName%22%3A%22circle%22%2C%22className%22%3A%22opacity-25%22%7D" className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path data-component-id="src/app/signup/page.jsx:2697:24" data-component-path="src/app/signup/page.jsx" data-component-line="2697" data-component-end-line="2697" data-component-file="page.jsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%2C%22className%22%3A%22opacity-75%22%7D" className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating Account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </button>
              )}
            </div>
          </div>
        </form>

        {/* Sign In Link */}
        <div data-component-id="src/app/signup/page.jsx:2711:8" data-component-path="src/app/signup/page.jsx" data-component-line="2711" data-component-end-line="2718" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-6%20text-center%22%7D" className="mt-6 text-center">
          <p data-component-id="src/app/signup/page.jsx:2712:10" data-component-path="src/app/signup/page.jsx" data-component-line="2712" data-component-end-line="2717" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-gray-300%22%2C%22textContent%22%3A%22Already%20have%20an%20account%3F%22%7D" className="text-gray-300">
            Already have an account?{' '}
            <Link data-component-id="src/app/signup/page.jsx:2714:12" data-component-path="src/app/signup/page.jsx" data-component-line="2714" data-component-end-line="2716" data-component-file="page.jsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Flogin%22%2C%22className%22%3A%22text-%5B%231d9b5d%5D%20hover%3Atext-%5B%2316844f%5D%20font-medium%20transition-colors%22%2C%22textContent%22%3A%22Sign%20in%22%7D" href="/login" className="text-[#1d9b5d] hover:text-[#16844f] font-medium transition-colors">
              Sign in
            </Link>
          </p>
        </div>

        {/* Security Notice */}
        <div data-component-id="src/app/signup/page.jsx:2721:8" data-component-path="src/app/signup/page.jsx" data-component-line="2721" data-component-end-line="2725" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-6%20text-center%22%7D" className="mt-6 text-center">
          <p data-component-id="src/app/signup/page.jsx:2722:10" data-component-path="src/app/signup/page.jsx" data-component-line="2722" data-component-end-line="2724" data-component-file="page.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-gray-400%22%2C%22textContent%22%3A%22%F0%9F%94%92%20Your%20information%20is%20protected%20with%20bank-level%20security%22%7D" className="text-xs text-gray-400">
            🔒 Your information is protected with bank-level security
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupNewPage;
